import {featureEach} from '@turf/meta';
import {Evented, FullscreenControl, LngLat, Map as Mapbox, MercatorCoordinate, NavigationControl} from 'mapbox-gl';
import AnimatedPopup from 'mapbox-gl-animated-popup';
import animation from './animation';
import Clock from './clock';
import configs from './configs';
import {ClockControl, LanguageControl, MapboxGLButtonControl, SearchControl} from './controls';
import Dataset from './dataset';
import {Airport, Exit, Flight, FlightStatus, Operator, POI, RailDirection, Railway, Station, Train, TrainTimetables, TrainType, TrainVehicleType} from './data-classes';
import extend from './extend';
import * as helpers from './helpers/helpers';
import {pickObject} from './helpers/helpers-deck';
import * as helpersGeojson from './helpers/helpers-geojson';
import * as helpersMapbox from './helpers/helpers-mapbox';
import {GeoJsonLayer, ThreeLayer, Tile3DLayer, TrafficLayer} from './layers';
import {loadBusData, loadDynamicBusData, loadDynamicFlightData, loadDynamicTrainData, loadLtaBusData, loadStaticData, loadTimetableData, updateOdptUrl} from './loader';
import BusArrivalPoller from './bus-arrival-poller';
import RailFeedPoller from './rail-feed-poller';
import {setCrowdDelayProvider} from './data-classes/train';
import {AboutPanel, BusPanel, LayerPanel, SharePanel, StationPanel, ThemePanel, TrackingModePanel, TrainPanel} from './panels';
import Plugin from './plugin';
import themes from './themes';
import nearestCloserPointOnLine from './turf/nearest-closer-point-on-line';

const DEGREE_TO_RADIAN = Math.PI / 180;

// Replace NavigationControl._updateZoomButtons to support disabling the control
const _updateZoomButtons = NavigationControl.prototype._updateZoomButtons;
NavigationControl.prototype._updateZoomButtons = function() {
    const me = this,
        {_disabled, _zoomInButton, _zoomOutButton, _compass} = me;

    _updateZoomButtons.apply(me);

    if (_disabled) {
        _zoomInButton.disabled = _zoomOutButton.disabled = true;
        _zoomInButton.setAttribute('aria-disabled', 'true');
        _zoomOutButton.setAttribute('aria-disabled', 'true');
    }
    _compass.disabled = !!_disabled;
    _compass.setAttribute('aria-disabled', (!!_disabled).toString());
};

NavigationControl.prototype.enable = function() {
    const me = this;

    delete me._disabled;
    me._updateZoomButtons();
};

NavigationControl.prototype.disable = function() {
    const me = this;

    me._disabled = true;
    me._updateZoomButtons();
};

export default class extends Evented {

    constructor(options) {
        super();

        const me = this;

        options = extend({
            hash: false,
            useWebGL2: true,
            center: configs.defaultCenter,
            zoom: configs.defaultZoom,
            bearing: configs.defaultBearing,
            pitch: configs.defaultPitch,
            dataUrl: configs.dataUrl,
            dataSources: configs.dataSources,
            clockControl: true,
            searchControl: false,
            languageControl: true,
            navigationControl: true,
            fullscreenControl: true,
            modeControl: false,
            configControl: true,
            trackingMode: configs.defaultTrackingMode,
            ecoMode: configs.defaultEcoMode,
            ecoFrameRate: configs.defaultEcoFrameRate
        }, options);

        me.lang = helpers.getLang(options.lang);
        me.dataUrl = options.dataUrl;
        me.dataSources = options.dataSources.map(({gtfsUrl, vehiclePositionUrl, color, type}) => ({
            gtfsUrl: updateOdptUrl(gtfsUrl, options.secrets),
            vehiclePositionUrl: updateOdptUrl(vehiclePositionUrl, options.secrets),
            color,
            type
        }));
        me.container = typeof options.container === 'string' ?
            document.getElementById(options.container) : options.container;
        me.secrets = options.secrets;
        me.modelOrigin = MercatorCoordinate.fromLngLat(options.center);
        me.exitPopups = [];

        me.clockControl = options.clockControl;
        me.searchControl = options.searchControl;
        me.languageControl = options.languageControl;
        me.navigationControl = options.navigationControl;
        me.fullscreenControl = options.fullscreenControl;
        me.modeControl = options.modeControl;
        me.configControl = options.configControl;
        me.clock = new Clock();
        me.plugins = (options.plugins || []).map(plugin => new Plugin(plugin));

        me.hiddenRailways = new Set();
        me.busLinesEnabled = false;
        me._busDataRequested = false;
        me.flightsEnabled = true;
        me.hiddenAirlines = new Set();
        me.searchMode = 'none';
        me.viewMode = configs.defaultViewMode;
        me.trackingMode = options.trackingMode;
        me.trackingParams = {
            zoom: {},
            bearing: {},
            pitch: {}
        };
        me.lastCameraParams = {};
        me.initialSelection = options.selection;
        me.clockMode = configs.defaultClockMode;
        me.isEditingTime = false;
        me.ecoMode = options.ecoMode;
        me.ecoFrameRate = options.ecoFrameRate;
        // Debug overlay toggle from options
        me.debugCounts = !!options.debugCounts;

        me.lastDynamicUpdate = {};
        me.lastRepaint = 0;
        me.frameRateFactor = 1;

        // The inner map container overrides the option
        options.container = initContainer(me.container);

        // This style overrides the option
        options.style = 'assets/style.json';

        // The custom attribution will be appended only if ConfigControl is visible
        if (!options.configControl) {
            options.customAttribution = helpers.flat([options.customAttribution, configs.customAttribution]);
        }

        me.map = new Mapbox(options);

        // Restrict map to Singapore and surrounding area
        me.map.setMaxBounds([[103.55, 1.1], [104.15, 1.5]]);
        me.map.setMinZoom(10);

        for (const event of configs.events) {
            me.map.on(event, me.fire.bind(me));
        }

        me.map.once('idle', () => {
            helpers.measureFrameRate().then(frameRate => {
                me.frameRateFactor = Math.min(60 / frameRate, 2);
            });
        });

        const clockPromise = options.center === configs.defaultCenter ?
            new Promise(resolve => resolve(me.clock)) :
            helpersMapbox.fetchTimezoneOffset(options.center, options.accessToken)
                .then(offset => me.clock.setTimezoneOffset(offset));

        Promise.all([
            loadStaticData(me.dataUrl, me.lang, clockPromise)
                .then(me.initData.bind(me))
                .catch(error => {
                    showErrorMessage(me.container);
                    throw error;
                }),
            new Promise(resolve => {
                me.map.once('styledata', resolve);
            })
        ]).then(me.initialize.bind(me));

        clockPromise.then(() => {
            me.gtfs = new Map();
            // Bus data is loaded lazily on first enableBusLines() call so
            // startup only fetches MRT/LRT data.
        });
    }

    /**
     * Returns the Mapbox's Map object used in the map.
     * @returns {mapboxgl.Map} The Mapbox's Map
     */
    getMapboxMap() {
        return this.map;
    }

    /**
     * Returns the map's geographical centerpoint.
     * @returns {LngLat} The map's geographical centerpoint
     */
    getCenter() {
        return this.map.getCenter();
    }

    /**
     * Sets the map's geographical centerpoint. Equivalent to jumpTo({center: center}).
     * @param {LngLatLike} center - The centerpoint to set
     * @returns {Map} Returns itself to allow for method chaining
     */
    setCenter(center) {
        const me = this;

        me.trackObject();
        me.map.setCenter(center);
        return me;
    }

    /**
     * Returns the map's current zoom level.
     * @returns {number} The map's current zoom level
     */
    getZoom() {
        return this.map.getZoom();
    }

    /**
     * Sets the map's zoom level. Equivalent to jumpTo({zoom: zoom}).
     * @param {number} zoom - The zoom level to set (0-20)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setZoom(zoom) {
        const me = this;

        me.map.setZoom(zoom);
        return me;
    }

    /**
     * Returns the map's current bearing. The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * @returns {number} The map's current bearing
     */
    getBearing() {
        return this.map.getBearing();
    }

    /**
     * Sets the map's bearing (rotation). The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * Equivalent to jumpTo({bearing: bearing}).
     * @param {number} bearing - The desired bearing
     * @returns {Map} Returns itself to allow for method chaining
     */
    setBearing(bearing) {
        const me = this;

        me.trackObject();
        me.map.setBearing(bearing);
        return me;
    }

    /**
     * Returns the map's current pitch (tilt).
     * @returns {number} The map's current pitch, measured in degrees away from the
     *     plane of the screen
     */
    getPitch() {
        return this.map.getPitch();
    }

    /**
     * Sets the map's pitch (tilt). Equivalent to jumpTo({pitch: pitch}).
     * @param {number} pitch - The pitch to set, measured in degrees away from the
     *     plane of the screen (0-60)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setPitch(pitch) {
        const me = this;

        me.map.setPitch(pitch);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, pitch, and padding with an
     * animated transition between old and new values. The map will retain its current
     * values for any details not specified in options.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions and AnimationOptions
     * @returns {Map} Returns itself to allow for method chaining
     */
    easeTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.easeTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, animating the
     * transition along a curve that evokes flight. The animation seamlessly incorporates
     * zooming and panning to help the user maintain her bearings even after traversing
     * a great distance.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions, AnimationOptions, and a few additional
     *     options
     * @returns {Map} Returns itself to allow for method chaining
     */
    flyTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.flyTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, without an animated
     * transition. The map will retain its current values for any details not specified
     * in options.
     * @param {CameraOptions} options - Options object
     * @returns {Map} Returns itself to allow for method chaining
     */
    jumpTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.jumpTo(options);
        return me;
    }

    /**
     * Returns the current view mode.
     * @returns {string} Current view mode: 'ground' or 'underground'
     */
    getViewMode() {
        return this.viewMode;
    }

    /**
     * Sets the view mode.
     * @param {string} mode - View mode: 'ground' or 'underground'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setViewMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setViewMode(mode);
        }
        return me;
    }

    /**
     * Returns the current tracking mode.
     * @returns {string} Current tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     */
    getTrackingMode() {
        return this.trackingMode;
    }

    /**
     * Sets the tracking mode.
     * @param {string} mode - Tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setTrackingMode(mode) {
        const me = this;

        me._setTrackingMode(mode);
        return me;
    }

    /**
     * Returns the ID of the train or flight being tracked, or the IDs of the selected
     * stations.
     * @returns {string | Array<string>} The ID of the train or flight being tracked, or
     *     the IDs of the selected stations
     */
    getSelection() {
        const trackedObject = this.trackedObject;

        if (isVehicle(trackedObject)) {
            return trackedObject.id;
        } else if (isStation(trackedObject)) {
            return trackedObject.stations;
        }
    }

    /**
     * Sets the ID of the train or flight you want to track, or the station to select.
     * @param {string} id - ID of the train or flight to be tracked, or the station to
     *     be selected
     * @returns {Map} Returns itself to allow for method chaining
     */
    setSelection(id) {
        const me = this,
            selection = helpers.removePrefix(id),
            station = me.stations.get(selection);

        if (station) {
            me.trackObject(me.stationGroupLookup.get(station.group));
            delete me.initialSelection;
        } else if (!selection.match(/NRT|HND/)) {
            if (me.trainLoaded) {
                let activeTrain;

                for (const id of me.timetables.getConnectingTrainIds(selection)) {
                    if ((activeTrain = me.activeTrainLookup.get(id))) {
                        break;
                    }
                }
                if (activeTrain) {
                    me.trackObject(activeTrain);
                } else {
                    helpers.showNotification(me.container, me.dict['train-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        } else {
            if (me.flightLoaded) {
                const activeFlight = me.activeFlightLookup.get(selection);

                if (activeFlight) {
                    me.trackObject(activeFlight);
                } else {
                    helpers.showNotification(me.container, me.dict['flight-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        }

        return me;
    }

    /**
     * Returns the current clock mode.
     * @returns {string} Current clock mode: 'realtime' or 'playback'
     */
    getClockMode() {
        return this.clockMode;
    }

    /**
     * Sets the clock mode.
     * @param {string} mode - Clock mode: 'realtime' or 'playback'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setClockMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setClockMode(mode);
        }
        return me;
    }

    /**
     * Returns the current eco mode.
     * @returns {string} Current eco mode: 'normal' or 'eco'
     */
    getEcoMode() {
        return this.ecoMode;
    }

    /**
     * Sets the eco mode.
     * @param {string} mode - Eco mode: 'normal' or 'eco'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setEcoMode(mode) {
        const me = this;

        me._setEcoMode(mode);
        return me;
    }

    /**
     * Adds a layer to the map.
     * @param {Object | CustomLayerInterface | GeoJsonLayerInterface | ThreeLayerInterface | Tile3DLayerInterface} layer
     *     - The layer to add, conforming to either the Mapbox Style Specification's
     *     layer definition, the CustomLayerInterface specification, the
     *     GeoJsonLayerInterface specification, the ThreeLayerInterface specification
     *     or the Tile3DLayerInterface specification
     * @param {string} beforeId - The ID of an existing layer to insert the new
     *     layer before
     * @returns {Map} Returns itself to allow for method chaining
     */
    addLayer(layer, beforeId) {
        const me = this;

        if (layer.type === 'three') {
            new ThreeLayer(layer).onAdd(me, beforeId);
        } else if (layer.type === 'geojson') {
            new GeoJsonLayer(layer).onAdd(me, beforeId);
        } else if (layer.type === 'tile-3d') {
            new Tile3DLayer(layer).onAdd(me, beforeId);
        } else {
            me.map.addLayer(layer, beforeId || 'poi');
        }
        return me;
    }

    /**
     * Refreshes the service-disruption line highlight and alert banner from
     * the current state of me.railFeedPoller. Called automatically whenever
     * the poller notifies of new data, but can also be called manually (e.g.
     * from the console, after stubbing me.railFeedPoller.state.alerts) to
     * force an immediate refresh.
     * @returns {Map} Returns itself to allow for method chaining
     */
    refreshAlertDisplay() {
        const me = this,
            {alerts} = me.railFeedPoller.state,
            affectedIds = alerts && alerts.status === 2 ?
                [...new Set(alerts.affected.map(({railwayId}) => railwayId).filter(id => id))] :
                [];

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const layerId = `railways-og-${zoom}`;

            if (!me.map.getLayer(layerId)) {
                continue;
            }
            me.map.setPaintProperty(layerId, 'line-color', affectedIds.length ? [
                'match',
                ['get', 'railway'],
                ...affectedIds.flatMap(id => [id, '#d32f2f']),
                ['get', 'color']
            ] : ['get', 'color']);
        }

        me.updateAlertBanner(alerts);

        return me;
    }

    /**
     * Shows, updates or hides the dismissible service-disruption banner at
     * the top of the map container based on the given alerts state.
     * @param {Object} alerts - me.railFeedPoller.state.alerts
     */
    updateAlertBanner(alerts) {
        const me = this,
            {dict, container} = me,
            hasMessages = alerts && alerts.status === 2 && alerts.messages && alerts.messages.length;

        if (!hasMessages) {
            if (me._alertBannerDiv) {
                me._alertBannerDiv.remove();
                me._alertBannerDiv = null;
            }
            me._dismissedAlertKey = null;
            return;
        }

        const key = alerts.messages.map(({content, createdAt}) => `${content}|${createdAt}`).join(';');

        if (key === me._dismissedAlertKey) {
            return;
        }

        if (!me._alertBannerDiv) {
            const banner = document.createElement('div'),
                label = document.createElement('span'),
                closeButton = document.createElement('span');

            banner.className = 'mt3d-alert-banner';
            label.className = 'mt3d-alert-banner-label';
            closeButton.className = 'mt3d-alert-banner-close';
            closeButton.textContent = '✕';
            closeButton.addEventListener('click', () => {
                me._dismissedAlertKey = key;
                banner.remove();
                me._alertBannerDiv = null;
            });
            banner.appendChild(label);
            banner.appendChild(closeButton);
            container.appendChild(banner);
            me._alertBannerDiv = banner;
        }

        me._alertBannerDiv.querySelector('.mt3d-alert-banner-label').textContent =
            `${dict['service-disruption']}: ${alerts.messages.map(({content}) => content).join(' ')}`;
    }

    /**
     * Refreshes the platform-crowd tint on station circle markers from the
     * current state of me.railFeedPoller (me.railFeedPoller.state.crowd, a
     * Map<stationCode, 'l'|'m'|'h'>). Called automatically whenever the
     * poller notifies of new data, but can also be called manually (e.g.
     * from the console, after stubbing me.railFeedPoller.state.crowd) to
     * force an immediate refresh.
     *
     * Station point features carry an `ids` array (the group's station IDs,
     * e.g. multiple for interchanges) rather than station codes, so rather
     * than rebuilding the feature data, this matches on `ids` membership via
     * a `case`/`in` paint expression, updated with setPaintProperty.
     * @returns {Map} Returns itself to allow for method chaining
     */
    refreshCrowdDisplay() {
        const me = this,
            crowdColors = {l: '#2e7d32', m: '#f9a825', h: '#c62828'},
            idsByColor = {};

        for (const station of me.stations.getAll()) {
            if (!station.code) {
                continue;
            }
            const level = me.railFeedPoller.getCrowdLevel(station.code),
                color = crowdColors[level];

            if (color) {
                (idsByColor[color] = idsByColor[color] || []).push(station.id);
            }
        }

        const colors = Object.keys(idsByColor),
            crowdCase = defaultExpr => colors.length ? [
                'case',
                ...colors.flatMap(color => [
                    ['any', ...idsByColor[color].map(id => ['in', id, ['get', 'ids']])],
                    color
                ]),
                defaultExpr
            ] : defaultExpr;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const defaults = {
                [`station-circles-og-${zoom}`]: ['case', ['==', ['get', 'dashed'], 1], '#e0e0e0', '#ffffff'],
                [`station-interchange-og-${zoom}`]: '#ffffff',
                [`station-circles-ug-${zoom}`]: '#ffffff',
                [`station-interchange-ug-${zoom}`]: '#ffffff'
            };

            for (const layerId of Object.keys(defaults)) {
                if (!me.map.getLayer(layerId)) {
                    continue;
                }
                me.map.setPaintProperty(layerId, 'circle-color', crowdCase(defaults[layerId]));
            }
        }

        return me;
    }

    /**
     * Removes the layer with the given ID from the map.
     * @param {string} id - ID of the layer to remove
     * @returns {Map} Returns itself to allow for method chaining
     */
    removeLayer(id) {
        const me = this;

        me.map.removeLayer(id);
        return me;
    }

    /**
     * Sets the visibility of the layer.
     * @param {string} layerId - The ID of the layer to set the visibility in.
     * @param {string} visibility - Whether this layer is displayed.
     *     'visible' and 'none' are supported.
     * @returns {Map} Returns itself to allow for method chaining
     */
    setLayerVisibility(layerId, visibility) {
        const me = this;

        me.map.setLayoutProperty(layerId, 'visibility', visibility);
        return me;
    }

    /**
     * Toggles visibility of a specific MRT/LRT railway line.
     * @param {string} railwayId - Railway ID (e.g. 'SMRT.NSL')
     * @param {boolean} visible - Whether the line should be visible
     */
    toggleRailway(railwayId, visible) {
        const me = this;

        if (visible) {
            me.hiddenRailways.delete(railwayId);
        } else {
            me.hiddenRailways.add(railwayId);
        }
        me._updateRailwayFilters();
    }

    /**
     * Batch toggles multiple railways at once.
     * @param {Array<{id: string, visible: boolean}>} changes - Railway visibility changes
     */
    toggleRailways(changes) {
        const me = this;

        for (const {id, visible} of changes) {
            if (visible) {
                me.hiddenRailways.delete(id);
            } else {
                me.hiddenRailways.add(id);
            }
        }
        me._updateRailwayFilters();
    }

    _updateRailwayFilters() {
        const me = this,
            hidden = [...me.hiddenRailways],
            hiddenExpr = hidden.length > 0 ? ['literal', hidden] : null;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            // Filter railway track lines (type 0, have 'railway' property)
            const railFilter = [
                'all',
                ['==', ['get', 'zoom'], zoom],
                ['==', ['get', 'type'], 0]
            ];
            if (hiddenExpr) {
                railFilter.push(['!', ['in', ['get', 'railway'], hiddenExpr]]);
            }
            if (me.map.getLayer(`railways-og-${zoom}`)) {
                me.map.setFilter(`railways-og-${zoom}`, railFilter);
            }

        }

        // Filter station code labels
        if (me.map.getLayer('station-codes-label')) {
            if (hiddenExpr) {
                me.map.setFilter('station-codes-label', ['!', ['in', ['get', 'railway'], hiddenExpr]]);
            } else {
                me.map.setFilter('station-codes-label', null);
            }
        }

        // Filter station circle/interchange layers
        // Compute visible railways (all railways minus hidden ones)
        const allRailwayIds = [...me.railways.getAll()].map(r => r.id);
        const visibleRailways = allRailwayIds.filter(id => !me.hiddenRailways.has(id));

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            for (const prefix of ['station-circles', 'station-interchange']) {
                for (const suffix of ['og', 'ug']) {
                    const layerId = `${prefix}-${suffix}-${zoom}`;

                    if (!me.map.getLayer(layerId)) {
                        continue;
                    }
                    const isInterchange = prefix === 'station-interchange' ? 1 : 0;
                    const baseFilter = [
                        'all',
                        ['==', ['get', 'zoom'], zoom],
                        ['==', ['get', 'interchange'], isInterchange]
                    ];

                    if (visibleRailways.length === allRailwayIds.length) {
                        // All visible
                        me.map.setFilter(layerId, baseFilter);
                    } else if (visibleRailways.length === 0) {
                        // None visible — hide all stations
                        me.map.setFilter(layerId, ['==', 1, 0]);
                    } else {
                        // Show station if ANY visible railway is in its railways list
                        baseFilter.push(['any',
                            ...visibleRailways.map(r => ['in', r, ['get', 'railways']])
                        ]);
                        me.map.setFilter(layerId, baseFilter);
                    }
                }
            }
        }

        // Hide/show deck.gl underground/route layers
        const allHidden = hidden.length > 0;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            for (const layerId of [
                `railways-ug-${zoom}`, `stations-ug-${zoom}`,
                `railways-routeug-${zoom}`, `stations-routeug-${zoom}`,
                `railways-routeog-${zoom}`, `stations-routeog-${zoom}`
            ]) {
                const layer = me.map.getLayer(layerId);

                if (layer) {
                    // For deck.gl layers, use opacity via setLayerProps
                    helpersMapbox.setLayerProps(me.map, layerId, {
                        opacity: allHidden ? 0 : undefined
                    });
                }
            }
        }

        // Also hide station-buildings when all railways hidden
        if (me.map.getLayer('station-buildings-fill')) {
            me.map.setLayoutProperty('station-buildings-fill', 'visibility', allHidden ? 'none' : 'visible');
        }

        // Hide/show 3D trains on hidden railways
        if (me.activeTrainLookup) {
            for (const train of me.activeTrainLookup.values()) {
                if (train.r && me.hiddenRailways.has(train.r.id)) {
                    me.stopTrain(train);
                }
            }
        }
    }

    /**
     * Sets the map visual theme.
     * @param {string} themeName - Theme key from themes.js
     */
    setTheme(themeName) {
        const me = this,
            theme = themes[themeName];

        if (!theme) {
            return;
        }

        me.currentTheme = themeName;
        me.themeUseDynamicLighting = theme.useDynamicLighting;

        const map = me.map;

        // Apply fog
        map.setFog(theme.fog);

        // Apply lighting
        if (!theme.useDynamicLighting) {
            const {ambient, directional, sky} = theme;

            map.setLights([{
                id: 'ambient',
                type: 'ambient',
                properties: {
                    color: `rgb(${ambient.r}, ${ambient.g}, ${ambient.b})`,
                    intensity: ambient.intensity
                }
            }, {
                id: 'directional',
                type: 'directional',
                properties: {
                    direction: ['literal', [210, 60]],
                    color: `rgb(${directional.r}, ${directional.g}, ${directional.b})`,
                    intensity: directional.intensity,
                    'cast-shadows': true,
                    'shadow-intensity': directional.shadowIntensity
                }
            }]);

            // Apply sky
            map.setPaintProperty('sky', 'sky-opacity', sky.opacity);
            map.setPaintProperty('sky', 'sky-atmosphere-color', sky.color);
            map.setPaintProperty('sky', 'sky-atmosphere-sun-intensity', sky.sunIntensity);
        } else {
            // Restore dynamic lighting from current time
            helpersMapbox.setSunlight(map, me.clock.getTime());
        }

        me.fire({type: 'theme'});
    }

    /**
     * Enables bus line/stop visualization.
     */
    enableBusLines() {
        const me = this;

        me.busLinesEnabled = true;

        if (!me._busDataRequested) {
            // First time enabling: lazily fetch bus/GTFS data, which was
            // skipped at startup so initial load only fetches MRT/LRT data.
            me._busDataRequested = true;
            me.fire({type: 'buslines-loading'});
            me.refreshBusData().then(() => {
                me.fire({type: 'buslines-loaded'});
            }).catch(error => {
                // Allow retrying on the next enable attempt
                me._busDataRequested = false;
                me.fire({type: 'buslines-loaded', error});
                console.error('Failed to load bus data:', error);
            });
            return;
        }

        me.refreshBuses();
        if (me.busArrivalPoller) {
            me.busArrivalPoller.start();
        }
    }

    /**
     * Disables bus line/stop visualization.
     */
    disableBusLines() {
        const me = this;

        me.busLinesEnabled = false;
        if (me.busArrivalPoller) {
            me.busArrivalPoller.stop();
        }
        for (const gtfs of me.gtfs.values()) {
            for (const bus of gtfs.activeBusLookup.values()) {
                me.stopBus(bus);
            }
        }
    }

    /**
     * Enables air traffic visualization.
     */
    enableFlights() {
        const me = this;

        me.flightsEnabled = true;
        me.refreshRealtimeFlightData();
    }

    /**
     * Disables air traffic visualization and stops all active flights.
     */
    disableFlights() {
        const me = this;

        me.flightsEnabled = false;
        for (const flight of me.activeFlightLookup.values()) {
            me.stopFlight(flight);
        }
    }

    /**
     * Toggles visibility of a specific airline's flights.
     * @param {string} airlineId - Operator ID (e.g., 'SIA', 'TGW')
     * @param {boolean} visible - Whether to show flights
     */
    toggleAirline(airlineId, visible) {
        const me = this;

        if (visible) {
            me.hiddenAirlines.delete(airlineId);
        } else {
            me.hiddenAirlines.add(airlineId);
            // Stop active flights for this airline
            for (const flight of me.activeFlightLookup.values()) {
                if (flight.a && flight.a.id === airlineId) {
                    me.stopFlight(flight);
                }
            }
        }
    }

    /**
     */
    getModelOrigin() {
        return this.modelOrigin;
    }

    /**
     * Returns the scale to transform into MercatorCoordinate from coordinates in
     * real world units using meters. This provides the distance of 1 meter in
     * MercatorCoordinate units at the initial centerpoint of the map.
     * @returns {number} The scale to transform into MercatorCoordinate from
     *     coordinates in real world units using meters
     */
    getModelScale() {
        return this.modelOrigin.meterInMercatorCoordinateUnits();
    }

    /**
     * Projects a LngLat to a MercatorCoordinate, and returns the translated
     * mercator coordinates with the initial centerpoint of the map as the origin.
     * @param {LngLatLike} lnglat - The location to project
     * @param {number} altitude - The altitude in meters of the position
     * @returns {Object} The translated mercator coordinates with the initial
     *     centerpoint of the map as the origin
     */
    getModelPosition(lnglat, altitude) {
        const me = this,
            coord = MercatorCoordinate.fromLngLat(lnglat, altitude);

        return {
            x: coord.x - me.modelOrigin.x,
            y: -(coord.y - me.modelOrigin.y),
            z: coord.z - me.modelOrigin.z
        };
    }

    /**
     * Checks if the background color of the map is dark.
     * @returns {boolean} True if the background color of the map is dark
     */
    hasDarkBackground() {
        return helpersMapbox.hasDarkBackground(this.map);
    }

    setDataSources(dataSources) {
        const me = this;

        me.dataSources = dataSources.map(({gtfsUrl, vehiclePositionUrl, color, type}) => ({
            gtfsUrl: updateOdptUrl(gtfsUrl, me.secrets),
            vehiclePositionUrl: updateOdptUrl(vehiclePositionUrl, me.secrets),
            color,
            type
        }));
        if (me._busDataRequested) {
            me.refreshBusData();
        }
    }

    initData(data) {
        const me = this,
            featureLookup = me.featureLookup = new Map(),
            stationGroupLookup = me.stationGroupLookup = new Map();

        me.dict = data.dict;
        me.featureCollection = data.featureCollection;

        me.stations = new Dataset(Station);
        me.railDirections = new Dataset(RailDirection, data.railDirectionData);
        me.railways = new Dataset(Railway, data.railwayData, {
            stations: me.stations,
            railDirections: me.railDirections
        });
        me.pois = new Dataset(POI, data.poiData);
        me.exits = new Dataset(Exit, data.exitData);
        me.stations.load(data.stationData, {
            stations: me.stations,
            railways: me.railways,
            railDirections: me.railDirections,
            pois: me.pois,
            exits: me.exits
        });

        // Build feature lookup dictionary and update feature properties
        featureEach(me.featureCollection, feature => {
            const properties = feature.properties,
                {group, altitude} = properties;

            // Skip station point markers (type 3) - they're only for circle rendering
            if (properties.type === 3) {
                return;
            }

            if (properties.type === 1) {
                // stations
                featureLookup.set(`${group}.${properties.zoom}`, feature);
                if (!stationGroupLookup.has(group)) {
                    stationGroupLookup.set(group, {
                        id: group,
                        type: 'station',
                        stations: properties.ids.map(id => me.stations.get(id)),
                        layer: altitude === 0 ? 'ground' : 'underground'
                    });
                }
            } else if (properties.section) {
                // railway sections - skip storing in featureLookup to avoid collision with zoom-based railway IDs
                // Section features are never retrieved from featureLookup, only railway features are used
                helpersGeojson.updateDistances(feature);
            } else if (properties.id) {
                // railway lines and airways (stored by id.zoom or just id)
                featureLookup.set(properties.id, feature);
                helpersGeojson.updateDistances(feature);
            }
        });

        for (const station of me.stations.getAll()) {
            if (station.alternate) {
                for (const layer of ['og', 'ug']) {
                    const key = station.group.replace(/.g$/, layer),
                        stationGroup = stationGroupLookup.get(key);

                    if (stationGroup) {
                        if (!stationGroup.hidden) {
                            stationGroup.hidden = [];
                        }
                        stationGroup.hidden.push(station);
                    }
                }
            }
        }

        me.trainTypes = new Dataset(TrainType, data.trainTypeData);
        me.trainVehicleTypes = new Dataset(TrainVehicleType, data.trainVehicleData);

        me.lastTimetableRefresh = me.clock.getTime('03:00');
        me.dataReferences = {
            railways: me.railways,
            stations: me.stations,
            railDirections: me.railDirections,
            trainTypes: me.trainTypes,
            trainVehicleTypes: me.trainVehicleTypes
        };
        me.timetables = new TrainTimetables(data.timetableData, me.dataReferences);

        me.operators = new Dataset(Operator, data.operatorData);
        me.airports = new Dataset(Airport, data.airportData);
        me.flightStatuses = new Dataset(FlightStatus, data.flightStatusData);

        // Store station buildings GeoJSON for visualization
        me.stationBuildingsData = data.stationBuildingsData;

        me.activeTrainLookup = new Map();
        me.standbyTrainLookup = new Map();
        me.realtimeTrains = new Set();
        me.adTrains = new Set();
        me.activeFlightLookup = new Map();
        me.flightLookup = new Map();
    }

    initialize() {
        const me = this,
            {lang, dict, clock, map, container, featureCollection} = me,
            initialZoom = map.getZoom(),
            layerZoom = me.layerZoom = getLayerZoom(initialZoom);

        if (map.loaded()) {
            hideLoader(container);
        } else {
            map.once('load', () => {
                hideLoader(container);
            });
        }

        // Deprecated
        // me.objectUnit = Math.max(getObjectScale(initialZoom) * .19, .02);

        me.trafficLayer = new TrafficLayer({id: 'traffic'});

        // Live rail feeds (service alerts + platform crowd density).
        me.railFeedPoller = new RailFeedPoller();
        me.railFeedPoller.onUpdate(() => {
            me.refreshAlertDisplay();
            me.refreshCrowdDisplay();
        });
        me.railFeedPoller.start();
        setCrowdDelayProvider(code => me.railFeedPoller.getCrowdLevel(code));

        // Create debug overlay if enabled
        if (me.debugCounts && !me.debugCountsDiv) {
            const dc = document.createElement('div');
            dc.id = 'mt3d-debug-counts';
            dc.style.cssText = 'position:fixed;z-index:9999;top:8px;left:8px;padding:6px 8px;background:rgba(0,0,0,0.6);color:#fff;font:12px/1.3 sans-serif;border-radius:4px;max-height:40vh;overflow:auto;white-space:nowrap;';
            dc.textContent = 'Loading counts...';
            me.container.appendChild(dc);
            me.debugCountsDiv = dc;
        }

        // To move to the style file in v4.0
        map.addLayer({
            id: 'sky',
            type: 'sky',
            paint: {
                'sky-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0,
                    0,
                    5,
                    0.3,
                    8,
                    1
                ],
                'sky-type': 'atmosphere',
                'sky-atmosphere-color': 'hsl(220, 100%, 70%)',
                'sky-atmosphere-sun-intensity': 20
            }
        }, 'background');
        helpersMapbox.setSunlight(map, clock.getTime());

        // Terrain disabled — focus on transport visualization
        // map.addSource('mapbox-dem', {
        //     type: 'raster-dem',
        //     url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        //     tileSize: 512,
        //     maxzoom: 14
        // });
        // map.setTerrain({source: 'mapbox-dem', exaggeration: 1.5});

        // Apply default theme
        me.themeUseDynamicLighting = true;
        me.setTheme(configs.defaultTheme);

        map.setLayoutProperty('poi', 'text-field', [
            'coalesce',
            ['get', `name_${lang}`],
            ['get', 'name_en'],
            ['get', 'name']
        ]);

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const commonProps = {
                type: 'geojson',
                lineWidthUnits: 'pixels',
                lineWidthScale:
                    zoom === 13 ? helpers.clamp(Math.pow(2, initialZoom - 12), .125, 1) :
                    zoom === 18 ? helpers.clamp(Math.pow(2, initialZoom - 19), 1, 8) : 1,
                parameters: {depthTest: false},
                minzoom: zoom <= 13 ? 0 : zoom,
                maxzoom: zoom >= 18 ? 24 : zoom + 1
            };

            for (const key of ['marked', 'selected']) {
                me.addLayer(Object.assign({}, commonProps, {
                    id: `stations-${key}-${zoom}`,
                    getLineWidth: 12,
                    getLineColor: [255, 255, 255],
                    getFillColor: [255, 255, 255],
                    visible: false
                }), 'trees');
            }

            for (const key2 of ['ug', 'routeug', 'routeog']) {
                for (const key1 of ['railways', 'stations']) {
                    const type = key1 === 'railways' ? 0 : 1,
                        filter = p => p.zoom === zoom && p.type === type && p.altitude < 0;

                    me.addLayer(Object.assign({}, commonProps, {
                        id: `${key1}-${key2}-${zoom}`,
                        data: key2 === 'ug' ?
                            helpersGeojson.featureFilter(featureCollection, filter) :
                            helpersGeojson.emptyFeatureCollection(),
                        transitions: {opacity: configs.transitionDuration}
                    }, {
                        'railways': {
                            filled: false,
                            getLineWidth: d => d.properties.width,
                            getLineColor: d => {
                                const rgba = helpers.colorToRGBArray(d.properties.color);
                                return [rgba[0], rgba[1], rgba[2], 180];
                            }
                        },
                        'stations': {
                            getLineWidth: 4,
                            getFillColor: [255, 255, 255, 140]
                        }
                    }[key1], {
                        'ug': {
                            opacity: 0,
                            pickable: key1 === 'stations',
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0,
                                'mt3d:opacity-route': 0,
                                'mt3d:opacity-underground': 1,
                                'mt3d:opacity-underground-route': 0.005
                            }
                        },
                        'routeug': {
                            opacity: 0,
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0.125,
                                'mt3d:opacity-underground': 1
                            }
                        },
                        'routeog': {
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-underground': 0.25
                            }
                        }
                    }[key2]), 'trees');
                }
            }
        }

        // Workaround for deck.gl #3522
        map.__deck.props.getCursor = () => map.getCanvas().style.cursor;

        map.addSource('odpt', {
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p => p.type !== 3 && (p.type === 0 || p.altitude === 0))
        });

        // Station center points source (type 3, above-ground only)
        map.addSource('station-points', {
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p => p.type === 3 && p.altitude === 0)
        });

        // Station center points source (type 3, underground only)
        map.addSource('station-points-ug', {
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p => p.type === 3 && p.altitude < 0)
        });

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const interpolate = ['interpolate', ['exponential', 2], ['zoom']],
                width = ['get', 'width'],
                color = ['get', 'color'],
                railWidth = ['*', width, 0.467],
                lineWidth =
                    zoom === 13 ? [...interpolate, 9, ['/', railWidth, 8], 12, railWidth] :
                    zoom === 18 ? [...interpolate, 19, railWidth, 22, ['*', railWidth, 8]] :
                    railWidth,
                stationOutlineWidth = ['*', width, 0.2],
                stationLineWidth =
                    zoom === 13 ? [...interpolate, 9, ['/', stationOutlineWidth, 8], 12, stationOutlineWidth] :
                    zoom === 18 ? [...interpolate, 19, stationOutlineWidth, 22, ['*', stationOutlineWidth, 8]] :
                    stationOutlineWidth;

            for (const key of ['railways']) {
                map.addLayer({
                    id: `${key}-og-${zoom}`,
                    type: 'line',
                    source: 'odpt',
                    filter: [
                        'all',
                        ['==', ['get', 'zoom'], zoom],
                        ['==', ['get', 'type'], 0]
                    ],
                    layout: {
                        visibility: zoom === layerZoom ? 'visible' : 'none'
                    },
                    paint: {
                        'line-color': color,
                        'line-width': lineWidth,
                        'line-opacity': 0.7,
                        'line-emissive-strength': 1,
                        'line-dasharray': [
                            'case',
                            ['==', ['get', 'dashed'], 1],
                            ['literal', [2, 2]],
                            ['literal', [1, 0]]
                        ]
                    },
                    metadata: {
                        'mt3d:opacity-effect': true,
                        'mt3d:opacity': 1,
                        'mt3d:opacity-route': 0.3,
                        'mt3d:opacity-underground': 0.25,
                        'mt3d:opacity-underground-route': 0.3
                    }
                }, 'trees');
            }
        }

        // Station circle markers (point-based, renders at correct terrain position)
        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const circleRadius =
                zoom === 13 ? ['interpolate', ['linear'], ['zoom'], 9, 2, 12, 5] :
                zoom === 18 ? ['interpolate', ['linear'], ['zoom'], 18, 5, 22, 20] :
                5;

            // Regular station circles
            map.addLayer({
                id: `station-circles-og-${zoom}`,
                type: 'circle',
                source: 'station-points',
                filter: [
                    'all',
                    ['==', ['get', 'zoom'], zoom],
                    ['==', ['get', 'interchange'], 0]
                ],
                layout: {
                    visibility: zoom === layerZoom ? 'visible' : 'none'
                },
                paint: {
                    'circle-radius': circleRadius,
                    'circle-color': ['case', ['==', ['get', 'dashed'], 1], '#e0e0e0', '#ffffff'],
                    'circle-opacity': ['case', ['==', ['get', 'dashed'], 1], 0.4, 0.6],
                    'circle-stroke-width': ['case', ['==', ['get', 'dashed'], 1], 1, 1.5],
                    'circle-stroke-color': ['case', ['==', ['get', 'dashed'], 1], '#999999', '#000000'],
                    'circle-stroke-opacity': ['case', ['==', ['get', 'dashed'], 1], 0.3, 0.5],
                    'circle-pitch-alignment': 'map',
                    'circle-translate': [0, -2],
                    'circle-emissive-strength': 1
                }
            }, 'trees');

            // Interchange station markers (larger)
            map.addLayer({
                id: `station-interchange-og-${zoom}`,
                type: 'circle',
                source: 'station-points',
                filter: [
                    'all',
                    ['==', ['get', 'zoom'], zoom],
                    ['==', ['get', 'interchange'], 1]
                ],
                layout: {
                    visibility: zoom === layerZoom ? 'visible' : 'none'
                },
                paint: {
                    'circle-radius': ['*', circleRadius, 1.4],
                    'circle-color': '#ffffff',
                    'circle-opacity': 0.7,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#333333',
                    'circle-stroke-opacity': 0.6,
                    'circle-pitch-alignment': 'map',
                    'circle-translate': [0, -2],
                    'circle-emissive-strength': 1
                }
            }, 'trees');
        }

        // Underground station circle markers
        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const circleRadius =
                zoom === 13 ? ['interpolate', ['linear'], ['zoom'], 9, 2, 12, 5] :
                zoom === 18 ? ['interpolate', ['linear'], ['zoom'], 18, 5, 22, 20] :
                5;

            map.addLayer({
                id: `station-circles-ug-${zoom}`,
                type: 'circle',
                source: 'station-points-ug',
                filter: [
                    'all',
                    ['==', ['get', 'zoom'], zoom],
                    ['==', ['get', 'interchange'], 0]
                ],
                layout: {
                    visibility: zoom === layerZoom ? 'visible' : 'none'
                },
                paint: {
                    'circle-radius': circleRadius,
                    'circle-color': '#ffffff',
                    'circle-opacity': 0.6,
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': '#000000',
                    'circle-stroke-opacity': 0.5,
                    'circle-pitch-alignment': 'map',
                    'circle-translate': [0, -2],
                    'circle-emissive-strength': 1
                },
                metadata: {
                    'mt3d:opacity-effect': true,
                    'mt3d:opacity': 0,
                    'mt3d:opacity-underground': 1
                }
            }, 'trees');

            map.addLayer({
                id: `station-interchange-ug-${zoom}`,
                type: 'circle',
                source: 'station-points-ug',
                filter: [
                    'all',
                    ['==', ['get', 'zoom'], zoom],
                    ['==', ['get', 'interchange'], 1]
                ],
                layout: {
                    visibility: zoom === layerZoom ? 'visible' : 'none'
                },
                paint: {
                    'circle-radius': ['*', circleRadius, 1.4],
                    'circle-color': '#ffffff',
                    'circle-opacity': 0.7,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#333333',
                    'circle-stroke-opacity': 0.6,
                    'circle-pitch-alignment': 'map',
                    'circle-translate': [0, -2],
                    'circle-emissive-strength': 1
                },
                metadata: {
                    'mt3d:opacity-effect': true,
                    'mt3d:opacity': 0,
                    'mt3d:opacity-underground': 1
                }
            }, 'trees');
        }

        me.addLayer(me.trafficLayer, 'trees');

        const routeData = [],
            colorData = [];

        for (const {id, color} of me.railways.getAll()) {
            const features = [13, 14, 15, 16, 17, 18].map(zoom => me.featureLookup.get(`${id}.${zoom}`));
            console.log(`Railway ${id}: features =`, features);
            routeData.push({
                id,
                feature: features
            });
            colorData.push({id, color});
        }
        for (const [id, feature] of me.featureLookup) {
            if (feature.properties.altitude > 0) {
                routeData.push({id, feature});
            }
        }
        for (const {id, color} of me.trainVehicleTypes.getAll()) {
            colorData.push({id, color});
        }
        for (const {id, color, tailcolor, stripecolor} of me.operators.getAll()) {
            colorData.push({id, color: [color, tailcolor, stripecolor || color]});
        }

        me.trafficLayer.addRouteGroup(routeData);
        me.trafficLayer.addColorGroup(colorData);

        // Add station building polygons layer
        if (me.stationBuildingsData && me.stationBuildingsData.features) {
            map.addSource('station-buildings', {
                type: 'geojson',
                data: me.stationBuildingsData
            });

            map.addLayer({
                id: 'station-buildings-fill',
                type: 'fill-extrusion',
                source: 'station-buildings',
                paint: {
                    'fill-extrusion-color': [
                        'case',
                        ['get', 'underground'],
                        '#4a90d9',  // Blue for underground
                        '#6bc06b'   // Green for aboveground
                    ],
                    'fill-extrusion-height': 8,
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 0.6
                },
                minzoom: 14
            }, 'trees');
        }

        // Add station exits layer as points
        const exitsFeatures = [];
        for (const exit of me.exits.getAll()) {
            exitsFeatures.push({
                type: 'Feature',
                properties: {
                    id: exit.id,
                    name: exit.name,
                    stationCode: exit.stationCode,
                    railway: exit.railway
                },
                geometry: {
                    type: 'Point',
                    coordinates: exit.coord
                }
            });
        }
        // Add source with empty data first, then set data to ensure proper array handling
        map.addSource('station-exits', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
        });

        // Set the actual data after source creation
        const exitsSource = map.getSource('station-exits');
        if (exitsSource && exitsFeatures.length > 0) {
            exitsSource.setData({
                type: 'FeatureCollection',
                features: exitsFeatures
            });
        }

        // Create exit marker icon using Font Awesome right-to-bracket icon (no leading whitespace)
        const exitIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><rect x="1" y="1" width="26" height="26" rx="4" fill="#333" stroke="#fff" stroke-width="1.5"/><g transform="translate(4, 5) scale(0.035)"><path fill="#fff" d="M217.9 105.9L340.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L217.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1L32 320c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM352 416l64 0c17.7 0 32-14.3 32-32l0-256c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l64 0c53 0 96 43 96 96l0 256c0 53-43 96-96 96l-64 0c-17.7 0-32-14.3-32-32s14.3-32 32-32z"/></g></svg>';

        const addExitLayer = () => {
            map.addLayer({
                id: 'station-exits-icon',
                type: 'symbol',
                source: 'station-exits',
                layout: {
                    'icon-image': 'exit-marker',
                    'icon-size': [
                        'interpolate', ['linear'], ['zoom'],
                        15, 0.6,
                        18, 1.0
                    ],
                    'icon-allow-overlap': true,
                    'text-field': ['concat', 'Exit ', ['get', 'name']],
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                    'text-size': [
                        'interpolate', ['linear'], ['zoom'],
                        16, 10,
                        18, 12
                    ],
                    'text-offset': [1.8, 0],
                    'text-anchor': 'left',
                    'text-optional': true
                },
                paint: {
                    'text-color': '#333333',
                    'text-halo-color': '#ffffff',
                    'text-halo-width': 1.5
                },
                minzoom: 15
            });
        };

        const exitIconImg = new Image(32, 32);
        exitIconImg.onload = () => {
            if (!map.hasImage('exit-marker')) {
                map.addImage('exit-marker', exitIconImg);
            }
            addExitLayer();
        };
        exitIconImg.onerror = () => {
            console.error('Failed to load exit marker icon');
        };
        exitIconImg.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(exitIconSvg);

        // Add station codes layer
        const stationCodesFeatures = [];
        for (const station of me.stations.getAll()) {
            if (station.code && station.coord && !station.alternate) {
                stationCodesFeatures.push({
                    type: 'Feature',
                    properties: {
                        id: station.id,
                        code: station.code,
                        color: station.railway ? station.railway.color : '#748477',
                        railway: station.railway ? station.railway.id : '',
                        name: station.title.en
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: station.coord
                    }
                });
            }
        }

        map.addSource('station-codes', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: stationCodesFeatures
            }
        });

        map.addLayer({
            id: 'station-codes-label',
            type: 'symbol',
            source: 'station-codes',
            minzoom: 14,
            layout: {
                'text-field': ['get', 'code'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 14, 10, 16, 12, 18, 14],
                'text-offset': [0, -1.5],
                'text-anchor': 'bottom',
                'text-allow-overlap': false,
                'text-ignore-placement': false
            },
            paint: {
                'text-color': '#ffffff',
                'text-halo-color': ['get', 'color'],
                'text-halo-width': 4,
                'text-halo-blur': 0
            }
        });

        /* For development
        me.addLayer({
            id: `airway-og-`,
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p =>
                p.type === 0 && p.altitude > 0
            ),
            filled: false,
            getLineWidth: d => d.properties.width,
            getLineColor: d => helpers.colorToRGBArray(d.properties.color),
            lineWidthUnits: 'pixels',
            lineWidthScale: 1,
            opacity: .0625,
            transitions: {opacity: configs.transitionDuration},
            parameters: {depthTest: false}
        });
        */

        me.styleOpacities = helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect');

        const datalist = helpers.createElement('datalist', {id: 'stations'}, document.body);
        const stationTitleLookup = me.stationTitleLookup = new Map();

        for (const l of [lang, 'en']) {
            for (const railway of me.railways.getAll()) {
                for (const station of railway.stations) {
                    const {title, utitle} = station,
                        stationTitle = (utitle && utitle[l]) || helpers.normalize(title[l] || title.en),
                        key = stationTitle.toUpperCase();

                    if (!stationTitleLookup.has(key)) {
                        helpers.createElement('option', {value: stationTitle}, datalist);
                        stationTitleLookup.set(key, station);
                    }
                }
            }
        }

        if (me.searchControl) {
            const control = new SearchControl({
                title: me.dict['search'],
                placeholder: me.dict['station-name'],
                list: 'stations',
                eventHandler: ({value}) => {
                    const station = me.stationTitleLookup.get(value.toUpperCase());

                    if (station && station.coord) {
                        me.markObject();
                        me.trackObject(me.stationGroupLookup.get(station.group));
                        return true;
                    }
                }
            });

            map.addControl(control);
        }

        if (me.languageControl) {
            const control = new LanguageControl({lang, langs: configs.langs});

            map.addControl(control, 'top-right');
        }

        if (me.navigationControl) {
            const control = me.navControl = new NavigationControl();

            control._setButtonTitle = function(button) {
                const {_zoomInButton, _zoomOutButton, _compass} = this,
                    title = button === _zoomInButton ? dict['zoom-in'] :
                    button === _zoomOutButton ? dict['zoom-out'] :
                    button === _compass ? dict['compass'] : '';

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            map.addControl(control);
        }

        if (me.fullscreenControl) {
            const control = new FullscreenControl({container});

            control._updateTitle = function() {
                const button = this._fullscreenButton,
                    title = dict[this._isFullscreen() ? 'exit-fullscreen' : 'enter-fullscreen'];

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            map.addControl(control);
        }

        if (me.modeControl) {
            const control = new MapboxGLButtonControl([{
                className: 'mapboxgl-ctrl-underground',
                title: dict['enter-underground'],
                eventHandler() {
                    me._setViewMode(me.viewMode === 'ground' ? 'underground' : 'ground');
                }
            }, {
                className: 'mapboxgl-ctrl-playback',
                title: dict['enter-playback'],
                eventHandler() {
                    me._setClockMode(me.clockMode === 'realtime' ? 'playback' : 'realtime');
                }
            }, {
                className: 'mapboxgl-ctrl-eco',
                title: dict['enter-eco'],
                eventHandler() {
                    me._setEcoMode(me.ecoMode === 'eco' ? 'normal' : 'eco');
                }
            }]);

            map.addControl(control);
        }

        me.layerPanel = new LayerPanel({
            layers: me.plugins,
            railways: [...me.railways.getAll()],
            airlines: [...me.operators.getAll()].filter(op => op.type === 'airline')
        });
        me.trackingModePanel = new TrackingModePanel();
        me.themePanel = new ThemePanel();
        me.aboutPanel = new AboutPanel();

        if (me.configControl) {
            map.addControl(new MapboxGLButtonControl([{
                className: 'mapboxgl-ctrl-layers',
                title: dict['select-layers'],
                eventHandler() {
                    me.layerPanel.addTo(me);
                }
            }, {
                className: 'mapboxgl-ctrl-theme',
                title: dict['select-theme'] || 'Theme',
                eventHandler() {
                    me.themePanel.addTo(me);
                }
            }, {
                className: 'mapboxgl-ctrl-rotate',
                title: 'Auto-rotate (R)',
                eventHandler(e) {
                    me.autoRotate = !me.autoRotate;
                    e.currentTarget.querySelector('.mapboxgl-ctrl-icon').classList.toggle('mapboxgl-ctrl-rotate-active', me.autoRotate);
                }
            }, {
                className: 'mapboxgl-ctrl-about',
                title: dict['about'],
                eventHandler() {
                    me.aboutPanel.addTo(me);
                }
            }]));
        }

        if (me.clockControl) {
            const control = me.clockCtrl = new ClockControl({lang, dict, clock});

            control.on('change', me.onClockChange.bind(me));
            map.addControl(control);
        }

        map.on('mousemove', e => {
            me.markObject(me.pickObject(e.point));
        });

        map.on('mouseout', () => {
            me.markObject();
        });

        map.on('click', e => {
            const object = me.pickObject(e.point);

            me.markObject(object);
            me.trackObject(object);

            // For development
            console.log(e.lngLat);
        });

        // WASD keyboard controls for smooth camera panning
        const keysDown = new Set();
        const velocity = {x: 0, y: 0, r: 0};
        const maxSpeed = 0.0004;
        const acceleration = 0.00004;
        const friction = 0.92;
        const rotAccel = 0.15;
        const rotFriction = 0.9;

        document.addEventListener('keydown', e => {
            const key = e.key.toLowerCase();

            if (['w', 'a', 's', 'd', 'q', 'e'].includes(key)) {
                keysDown.add(key);
                e.preventDefault();
            }
            // Toggle auto-rotation with R key
            if (key === 'r') {
                me.autoRotate = !me.autoRotate;
                const icon = container.querySelector('.mapboxgl-ctrl-rotate .mapboxgl-ctrl-icon');
                if (icon) {
                    icon.classList.toggle('mapboxgl-ctrl-rotate-active', me.autoRotate);
                }
            }
        });

        document.addEventListener('keyup', e => {
            keysDown.delete(e.key.toLowerCase());
        });

        const processKeys = () => {
            const zoom = map.getZoom();
            const zoomFactor = Math.pow(2, 15 - zoom);

            // Accelerate based on keys
            if (!me.trackedObject) {
                if (keysDown.has('w')) { velocity.y += acceleration * zoomFactor; }
                if (keysDown.has('s')) { velocity.y -= acceleration * zoomFactor; }
                if (keysDown.has('a')) { velocity.x -= acceleration * zoomFactor; }
                if (keysDown.has('d')) { velocity.x += acceleration * zoomFactor; }
                if (keysDown.has('q')) { velocity.r -= rotAccel; }
                if (keysDown.has('e')) { velocity.r += rotAccel; }
            }

            // Clamp speed
            const maxSpd = maxSpeed * zoomFactor;

            velocity.x = Math.max(-maxSpd, Math.min(maxSpd, velocity.x));
            velocity.y = Math.max(-maxSpd, Math.min(maxSpd, velocity.y));
            velocity.r = Math.max(-3, Math.min(3, velocity.r));

            // Apply friction
            velocity.x *= friction;
            velocity.y *= friction;
            velocity.r *= rotFriction;

            // Apply movement
            if (Math.abs(velocity.x) > 0.000001 || Math.abs(velocity.y) > 0.000001) {
                const bearing = map.getBearing() * Math.PI / 180;
                const cos = Math.cos(-bearing), sin = Math.sin(-bearing);
                const lng = velocity.x * cos - velocity.y * sin;
                const lat = velocity.x * sin + velocity.y * cos;
                const center = map.getCenter();

                map.setCenter([center.lng + lng, center.lat + lat]);
            }
            if (Math.abs(velocity.r) > 0.01) {
                map.setBearing(map.getBearing() + velocity.r);
            }

            requestAnimationFrame(processKeys);
        };
        requestAnimationFrame(processKeys);

        // Auto-rotation
        me.autoRotate = false;
        me.autoRotateSpeed = 0.05; // degrees per frame

        map.on('render', () => {
            if (me.autoRotate && !me.trackedObject) {
                map.setBearing(map.getBearing() + me.autoRotateSpeed);
            }
        });

        map.on('zoom', e => {
            if (!e.tracking) {
                me.updateBaseZoom();
            }

            const zoom = map.getZoom(),
                prevLayerZoom = me.layerZoom,
                layerZoom = me.layerZoom = getLayerZoom(zoom);

            if (zoom < 13) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 12), .125, 1);

                for (const id of ['stations-marked-13', 'stations-selected-13', 'railways-ug-13', 'stations-ug-13', 'railways-routeug-13', 'stations-routeug-13', 'railways-routeog-13', 'stations-routeog-13']) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            } else if (zoom > 19) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 19), 1, 8);

                for (const id of ['stations-marked-18', 'stations-selected-18', 'railways-ug-18', 'stations-ug-18', 'railways-routeug-18', 'stations-routeug-18', 'railways-routeog-18', 'stations-routeog-18']) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            }

            // Deprecated
            // me.objectUnit = Math.max(getObjectScale(zoom) * .19, .02);

            if (prevLayerZoom !== layerZoom) {
                for (const key of ['railways']) {
                    me.setLayerVisibility(`${key}-og-${prevLayerZoom}`, 'none');
                    me.setLayerVisibility(`${key}-og-${layerZoom}`, 'visible');
                }
                for (const key of ['station-circles', 'station-interchange']) {
                    me.setLayerVisibility(`${key}-og-${prevLayerZoom}`, 'none');
                    me.setLayerVisibility(`${key}-og-${layerZoom}`, 'visible');
                    me.setLayerVisibility(`${key}-ug-${prevLayerZoom}`, 'none');
                    me.setLayerVisibility(`${key}-ug-${layerZoom}`, 'visible');
                }

                // Re-apply railway filters to newly visible layers
                if (me.hiddenRailways.size > 0) {
                    me._updateRailwayFilters();
                }

                for (const {id} of me.gtfs.values()) {
                    for (const key of ['busstops', 'busstops-outline']) {
                        if (prevLayerZoom >= 14) {
                            me.setLayerVisibility(`${key}-${id}-og-${prevLayerZoom}`, 'none');
                        }
                        if (layerZoom >= 14) {
                            me.setLayerVisibility(`${key}-${id}-og-${layerZoom}`, 'visible');
                        }
                    }
                }

                if (e.tracking) {
                    me.prevLayerZoom = prevLayerZoom;
                } else {
                    delete me.prevLayerZoom;
                }
            }
        });

        map.on('render', () => {
            if (me.markedObject) {
                // Popup for a 3D object needs to be updated every time
                // because the adjustment for altitude is required
                me.updatePopup();
            }
            me.updateAdTrainPopup();
        });

        for (const plugin of me.plugins.slice().reverse()) {
            plugin.addTo(me);
        }

        animation.init();

        animation.start({
            callback: () => {
                const clock = me.clock,
                    now = clock.getTime(),
                    {minDelay, refreshInterval, realtimeCheckInterval, flightCheckInterval} = configs;

                if (now - me.lastTimetableRefresh >= 86400000) {
                    me.refreshTrainTimetableData();
                    if (me._busDataRequested) {
                        me.refreshBusData(true);
                    }
                    me.lastTimetableRefresh = clock.getTime('03:00');
                }

                // Remove all trains if the page has been invisible for certain amount of time
                if (Date.now() - me.lastFrameRefresh >= configs.refreshTimeout) {
                    me.stopAll();
                }
                me.lastFrameRefresh = Date.now();

                if (Math.floor((now - minDelay) / refreshInterval) !== Math.floor(me.lastRefresh / refreshInterval)) {
                    // Hide building models when the clock speed is high as changing lights impacts performance
                    map.setLayoutProperty('building-models', 'visibility', clock.speed <= 30 ? 'visible' : 'none');

                    helpersMapbox.setSunlight(map, now, !me.themeUseDynamicLighting);
                    if (me.searchMode === 'none' && me.clockMode === 'playback' && !me.removing) {
                        me.refreshTrains();
                        me.refreshFlights();
                        me.refreshBuses();
                        if (isStation(me.trackedObject)) {
                            me.detailPanel.updateContent();
                        }
                    }
                    me.lastRefresh = now - minDelay;
                }

                if (Math.floor((now - minDelay) / realtimeCheckInterval) !== Math.floor(me.lastRealtimeCheck / realtimeCheckInterval)) {
                    if (me.searchMode === 'none' && me.clockMode === 'realtime' && !me.removing) {
                        me.refreshRealtimeTrainData();
                        me.refreshRealtimeBusData();
                        if (isStation(me.trackedObject)) {
                            me.detailPanel.updateContent();
                        }
                    }
                    me.lastRealtimeCheck = now - minDelay;
                }

                // Flight data refreshes on a slower cadence (5 min) to stay within API limits
                if (Math.floor((now - minDelay) / flightCheckInterval) !== Math.floor((me.lastFlightCheck || 0) / flightCheckInterval)) {
                    if (me.searchMode === 'none' && me.clockMode === 'realtime' && !me.removing) {
                        me.refreshRealtimeFlightData();
                    }
                    me.lastFlightCheck = now - minDelay;
                }

                if ((me.ecoMode === 'normal' && map._loaded) || Date.now() - me.lastRepaint >= 1000 / me.ecoFrameRate) {
                    let changed;

                    me.trafficLayer.setTimeOffset(clock.getTimeOffset());

                    if (me.markedObject && isVehicle(me.markedObject)) {
                        changed = me.updateObjectPosition(me.markedObject);
                        if (changed.standing !== undefined) {
                            me.updatePopup({setHTML: true});
                        }
                    }

                    if (me.trackedObject && isVehicle(me.trackedObject)) {
                        if (me.trackedObject !== me.markedObject) {
                            changed = me.updateObjectPosition(me.trackedObject);
                        }
                        if (changed.viewMode) {
                            me._setViewMode(changed.viewMode);
                        }
                        if (!me.viewAnimationID && !map._zooming && !map._rotating && !map._pitching) {
                            const {coord, altitude, bearing} = me.trackedObject;

                            me._jumpTo({center: coord, altitude, bearing, bearingFactor: .02});
                            if (me.markedObject === me.trackedObject) {
                                // This need to be called right after jumpTo() to prevent jittering
                                me.updatePopup();
                            }
                        }
                    }

                    for (const train of me.adTrains) {
                        me.updateObjectPosition(train);
                    }

                    if (!isVehicle(me.trackedObject)) {
                        if (me.trackedObject) {
                            me.refreshStationOutline();
                        }
                    }

                    map.triggerRepaint();
                    me.lastRepaint = Date.now();
                }
            }
        });

        me.initialized = true;
        me.fire({type: 'initialized'});
    }

    _jumpTo(options) {
        const me = this,
            {map, trackingMode, trackingParams} = me,
            frameRateFactor = me.ecoMode === 'normal' || me.viewAnimationID ? me.frameRateFactor : 60 / me.ecoFrameRate,
            now = performance.now(),
            currentZoom = map.getZoom(),
            currentBearing = map.getBearing(),
            currentPitch = map.getPitch(),
            scrollZooming = map.scrollZoom._active;
        let zoom, pitch,
            {center, altitude, bearing, easeOutFactor, easeInFactor, bearingFactor} = options;

        if (trackingMode === 'position') {
            zoom = me.baseZoom;
            bearing = currentBearing;
            pitch = currentPitch;
        } else if (trackingMode === 'helicopter') {
            zoom = 15;
            bearing = trackingParams.bearing.fn(now);
            pitch = 60;
        } else if (trackingMode === 'drone') {
            zoom = 17;
            bearing = trackingParams.bearing.fn(now);
            pitch = 75;
        } else if (trackingMode === 'bird') {
            me.updateTrackingParams();
            zoom = trackingParams.zoom.fn(now);
            bearing = trackingParams.bearing.fn(now);
            pitch = trackingParams.pitch.fn(now);
        } else {
            if (trackingMode === 'front' || trackingMode === 'topfront') {
                bearing = (bearing + 360) % 360 - 180;
            }
            if (bearingFactor >= 0) {
                bearingFactor = 1 - Math.pow(1 - bearingFactor, frameRateFactor);
                bearing = currentBearing + ((bearing - currentBearing + 540) % 360 - 180) * bearingFactor;
            }
            if (trackingMode === 'back' || trackingMode === 'front') {
                zoom = 18.5;
                pitch = 85;
            } else {
                zoom = 15;
                pitch = 60;
            }
        }

        const cameraToGround = map.getFreeCameraOptions().position.z / Math.cos(currentPitch * DEGREE_TO_RADIAN),
            cameraToObject = cameraToGround * Math.pow(2, currentZoom - zoom),
            objectToGround = me.getModelPosition(center, altitude).z / Math.cos(pitch * DEGREE_TO_RADIAN);

        // Adjust zoom according to the altitude of the object
        zoom = Math.min(zoom - Math.log2(Math.max(cameraToObject + objectToGround, 0) / cameraToObject), 22);

        center = me.adjustCoord(center, altitude, bearing);
        if (easeOutFactor >= 0) {
            const currentCenter = map.getCenter();

            easeOutFactor = 1 - Math.pow(1 - easeOutFactor, frameRateFactor);
            center = new LngLat(
                helpers.lerp(currentCenter.lng, center.lng, easeOutFactor),
                helpers.lerp(currentCenter.lat, center.lat, easeOutFactor)
            );
        }
        if (easeInFactor >= 0) {
            easeInFactor = 1 - Math.pow(1 - easeInFactor, frameRateFactor);
            zoom = helpers.lerp(currentZoom, zoom, easeInFactor);
            pitch = helpers.lerp(currentPitch, pitch, easeInFactor);
        }

        // Prevent layer switch due to calculation error
        if (Math.floor(zoom + 1) - zoom < 1e-6) {
            zoom = Math.floor(zoom + 1);
        }

        // Prevent layer switch back and forth
        if (me.prevLayerZoom === getLayerZoom(zoom)) {
            zoom = currentZoom;
        } else {
            delete me.prevLayerZoom;
        }

        map.jumpTo({center, zoom, bearing, pitch}, {tracking: true});

        // Workaround for the issue of the scroll zoom during tracking
        if (scrollZooming) {
            map.scrollZoom._active = true;
        }
    }

    refreshTrains() {
        const me = this,
            initialSelection = me.initialSelection,
            now = me.clock.getTimeOffset();

        for (const timetable of me.timetables.getAll()) {
            if (timetable.start <= now && now <= timetable.end && !me.standbyTrainLookup.has(timetable.id)) {
                me.trainStart(new Train(timetable));
            }
        }

        me.trafficLayer.refreshDelayMarkers();

        me.trainLoaded = true;
        if (initialSelection) {
            me.setSelection(initialSelection);
        }
        if (me.debugCountsDiv) {
            me._updateDebugCounts();
        }
    }

    trainStart(train, options = {}) {
        const me = this,
            railway = train.r;

        if (me.checkActiveTrains(train) || (railway.status && railway.dynamic && !me.realtimeTrains.has(train.id)) || railway.suspended || me.hiddenRailways.has(railway.id)) {
            return;
        }
        if (!me.setSectionData(train, options.index)) {
            return; // Out of range
        }

        me.activeTrainLookup.set(train.id, train);
        me.trafficLayer.addObject(train);
        me.trainRepeat(train, options.index);

        if (options.marked) {
            me.markObject(train);
        }
        if (options.tracked) {
            me.trackObject(train);
        }
        if (train.ad) {
            me.showAdTrainPopup(train);
        }
    }

    trainRepeat(train, index) {
        const me = this,
            {activeTrainLookup, clock} = me,
            timetable = train.timetable,
            now = clock.getTimeOffset(),
            minStandingDuration = configs.minStandingDuration;
        let final;

        if (timetable) {
            final = !me.setSectionData(train, index);
            if (final) {
                const nextTimetables = timetable.nt;

                if (nextTimetables) {
                    let needToStand = false;

                    for (const {t: id} of nextTimetables[0].pt) {
                        const prevTrain = activeTrainLookup.get(id);

                        if (prevTrain && prevTrain.arrivalStation) {
                            needToStand = true;
                        }
                    }
                    if (!needToStand) {
                        let marked = false,
                            tracked = false;

                        for (const {t: id} of nextTimetables[0].pt) {
                            const prevTrain = activeTrainLookup.get(id);

                            if (prevTrain) {
                                if (me.markedObject === prevTrain) {
                                    marked = true;
                                }
                                if (me.trackedObject === prevTrain) {
                                    tracked = true;
                                }
                                me.stopTrain(prevTrain);
                            }
                        }
                        nextTimetables.forEach((nextTimetable, i) => {
                            const nextTrain = me.standbyTrainLookup.get(nextTimetable.id) || new Train(nextTimetable);

                            me.trainStart(nextTrain, i === 0 ? {index: 0, marked, tracked} : {index: 0});
                        });
                    }
                    return;
                }
            }
        } else {
            final = !me.setSectionData(train, undefined, !me.realtimeTrains.has(train.id));
        }

        if (me.markedObject === train) {
            train.standing = true;
            me.updatePopup({setHTML: true});
        }

        const sectionLength = train.sectionLength;

        if (!final && sectionLength !== 0) {
            const delay = train.delay || 0,
                minDelay = configs.minDelay,
                {departureTime, arrivalTime, nextDepartureTime, sectionIndex} = train,
                feature = me.featureLookup.get(`${train.r.id}.18`),
                stationOffsets = feature.properties['station-offsets'],
                distance = Math.abs(stationOffsets[sectionIndex + sectionLength] - stationOffsets[sectionIndex]),
                actualDepartureTime = index !== undefined ? Math.max(departureTime + delay, now + (clock.speed === 1 ? minStandingDuration + train.getCrowdDelay() : 0)) : departureTime !== undefined ? departureTime + delay : now;
            let {maxSpeed, acceleration, maxAccelerationTime, maxAccDistance} = configs,
                duration, minDuration, maxDuration, accelerationTime;

            if (nextDepartureTime !== undefined) {
                maxDuration = nextDepartureTime - minStandingDuration + 60000 + delay - minDelay - actualDepartureTime;
            }
            if (arrivalTime !== undefined) {
                minDuration = arrivalTime + delay - minDelay - actualDepartureTime;
                if (!(maxDuration < minDuration + 60000)) {
                    maxDuration = minDuration + 60000;
                }
            }

            if (distance <= maxAccDistance * 2) {
                duration = Math.sqrt(distance / acceleration) * 2;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, minDuration || 0, maxDuration);
                    acceleration = distance * 4 / duration / duration;
                }
                accelerationTime = duration / 2;
            } else {
                duration = maxAccelerationTime * 2 + (distance - maxAccDistance * 2) / maxSpeed;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, minDuration || 0, maxDuration);
                    maxAccDistance = acceleration * duration * duration / 8;
                    if (distance >= maxAccDistance * 2) {
                        maxSpeed = distance * 2 / duration;
                        acceleration = maxSpeed * 2 / duration;
                    } else {
                        maxSpeed = acceleration * duration / 2 - Math.sqrt(acceleration * (maxAccDistance * 2 - distance));
                    }
                }
                accelerationTime = maxSpeed / acceleration;
            }
            me.trafficLayer.updateObject(train, actualDepartureTime, duration, accelerationTime, acceleration / distance, accelerationTime, acceleration / distance);

            train.animationID = animation.start({
                complete: () => {
                    me.trainRepeat(train, timetable ? train.timetableIndex + 1 : undefined);
                },
                duration: actualDepartureTime + duration - now,
                clock
            });
        } else {
            train.animationID = animation.start({
                complete: () => {
                    if (final) {
                        me.stopTrain(train);
                    } else {
                        me.trainRepeat(train);
                    }
                },
                duration: timetable ?
                    Math.max(train.departureTime - now, minStandingDuration) :
                    final ? minStandingDuration : configs.realtimeCheckInterval,
                clock
            });
        }
    }

    refreshFlights() {
        const me = this,
            initialSelection = me.initialSelection,
            now = me.clock.getTimeOffset();

        for (const flight of me.flightLookup.values()) {
            if (flight.entry <= now && now <= flight.end) {
                me.flightStart(flight);
            }
        }

        me.flightLoaded = true;
        if (initialSelection) {
            me.setSelection(initialSelection);
        }
    }

    flightStart(flight) {
        const me = this,
            {activeFlightLookup, clock} = me,
            id = flight.id;

        if (activeFlightLookup.has(id)) {
            return;
        }

        // Skip if flights disabled or airline hidden
        if (!me.flightsEnabled) {
            return;
        }
        if (flight.a && me.hiddenAirlines.has(flight.a.id)) {
            return;
        }

        activeFlightLookup.set(id, flight);
        me.trafficLayer.addObject(flight);

        const now = clock.getTimeOffset(),
            distance = flight.feature.properties.length,
            maxSpeed = flight.maxSpeed,
            acceleration = flight.acceleration > 0 ? flight.acceleration : 0,
            deceleration = flight.acceleration > 0 ? 0 : -flight.acceleration,
            accelerationTime = acceleration > 0 ? maxSpeed / acceleration : 0,
            decelerationTime = deceleration > 0 ? maxSpeed / deceleration : 0,
            duration = accelerationTime / 2 + distance / maxSpeed + decelerationTime / 2;

        me.trafficLayer.updateObject(flight, flight.start, duration, accelerationTime, acceleration / distance, decelerationTime, deceleration / distance);

        flight.animationID = animation.start({
            complete: () => {
                me.stopFlight(flight);
            },
            duration: flight.end - now,
            clock
        });
    }

    refreshBuses(gtfsId) {
        const me = this,
            now = me.clock.getTimeOffset();

        for (const gtfs of gtfsId ? [me.gtfs.get(gtfsId)] : me.gtfs.values()) {
            for (const trip of gtfs.tripLookup.values()) {
                const departureTimes = trip.departureTimes,
                    route = gtfs.routeLookup.get(trip.route);

                if (departureTimes[0] <= now && now <= departureTimes[departureTimes.length - 1] && !gtfs.activeBusLookup.has(trip.id) && route && !route.hidden) {
                    let offset = 0;
                    const feature = gtfs.featureLookup.get(trip.shape);

                    if (!feature) {
                        continue;
                    }

                    const offsets = trip.stops.map(stopId =>
                        // Use the previous offset to calulate a weight and pick a closer point
                        (offset = nearestCloserPointOnLine(feature, gtfs.stopLookup.get(stopId).coord, offset).properties.location)
                    );

                    me.busStart({
                        type: 'bus',
                        gtfsId: gtfs.id,
                        trip,
                        feature,
                        offsets,
                        offset: 0,
                        operatorColorId: route ? `${gtfs.id}.${route.operator || ''}` : gtfs.id,
                        load: simulateBusLoad(now, trip)
                    });
                }
            }
        }
    }

    busStart(bus) {
        const me = this;

        if (!me.busLinesEnabled) {
            return;
        }
        if (!me.setBusSectionData(bus)) {
            return;
        }
        me.gtfs.get(bus.gtfsId).activeBusLookup.set(bus.trip.id, bus);
        me.trafficLayer.addObject(bus);
        me.busRepeat(bus);
    }

    busRepeat(bus, index) {
        const me = this,
            clock = me.clock,
            now = clock.getTimeOffset(),
            minBusStandingDuration = configs.minBusStandingDuration;
        let final;

        if (bus.stop === undefined) {
            final = !me.setBusSectionData(bus, index);
        } else {
            final = !me.setBusSectionData(bus, undefined, !me.gtfs.get(bus.gtfsId).realtimeBuses.has(bus.trip.id));
        }

        if (me.markedObject === bus) {
            me.updatePopup({setHTML: true});
        }
        if (me.trackedObject === bus && me.detailPanel) {
            me.detailPanel.updateHeader();
        }

        const {offsets, sectionIndex, sectionLength} = bus,
            distance = Math.abs(offsets[sectionIndex + sectionLength] - offsets[sectionIndex]);

        // Sometimes distance becomes 0 because the busroute coordinates
        // are incorrect and a few busstops shares the same coordinates.
        if (!final && sectionLength > 0 && distance === 0) {
            console.log('bus zero interval:', bus);
        }

        if (!final && sectionLength > 0 && distance > 0) {
            const minDelay = configs.minDelay,
                {departureTime, nextDepartureTime} = bus,
                actualDepartureTime = index !== undefined ? Math.max(departureTime, now + (clock.speed === 1 ? minBusStandingDuration : 0)) : helpers.valueOrDefault(departureTime, now);
            let {maxBusSpeed, busAcceleration, maxBusAccelerationTime, maxBusAccDistance} = configs,
                duration, maxDuration, accelerationTime;

            if (nextDepartureTime !== undefined) {
                maxDuration = nextDepartureTime - minBusStandingDuration + 60000 - minDelay - actualDepartureTime;
            }

            if (distance <= maxBusAccDistance * 2) {
                duration = Math.sqrt(distance / busAcceleration) * 2;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, 0, maxDuration);
                    busAcceleration = distance * 4 / duration / duration;
                }
                accelerationTime = duration / 2;
            } else {
                duration = maxBusAccelerationTime * 2 + (distance - maxBusAccDistance * 2) / maxBusSpeed;
                if (maxDuration > 0) {
                    duration = helpers.clamp(duration, 0, maxDuration);
                    maxBusAccDistance = busAcceleration * duration * duration / 8;
                    if (distance >= maxBusAccDistance * 2) {
                        maxBusSpeed = distance * 2 / duration;
                        busAcceleration = maxBusSpeed * 2 / duration;
                    } else {
                        maxBusSpeed = busAcceleration * duration / 2 - Math.sqrt(busAcceleration * (maxBusAccDistance * 2 - distance));
                    }
                }
                accelerationTime = maxBusSpeed / busAcceleration;
            }
            me.trafficLayer.updateObject(bus, actualDepartureTime, duration, accelerationTime, busAcceleration / distance, accelerationTime, busAcceleration / distance);

            bus.animationID = animation.start({
                complete: () => {
                    me.busRepeat(bus, bus.stop === undefined ? bus.sectionIndex + 1 : undefined);
                },
                duration: actualDepartureTime + duration - now,
                clock
            });
        } else {
            bus.animationID = animation.start({
                complete: () => {
                    if (final) {
                        me.stopBus(bus);
                    } else {
                        me.busRepeat(bus);
                    }
                },
                duration: bus.stop === undefined ?
                    Math.max(bus.nextDepartureTime - now, minBusStandingDuration) :
                    final ? minBusStandingDuration : configs.realtimeCheckInterval,
                clock
            });
        }
    }

    updateTrackingParams(reset) {
        const me = this,
            {map, trackingMode, trackingParams} = me,
            now = performance.now();

        if (trackingMode === 'bird') {
            const {zoom, bearing, pitch} = trackingParams;

            if (!zoom.time || reset) {
                const time = zoom.time = [0, now, 0, 0],
                    value = zoom.value = [0, me.baseZoom, 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 30000);
                    value[i] = Math.random() * 5 + 15;
                }
                zoom.fn = createInterpolant(time, value);
            } else if (now >= zoom.time[2]) {
                zoom.time = zoom.time.slice(1).concat(zoom.time[3] + Math.random() * 10000 + 30000);
                zoom.value = zoom.value.slice(1).concat(Math.random() * 5 + 15);
                zoom.fn = createInterpolant(zoom.time, zoom.value);
            }
            if (!bearing.time || reset) {
                const time = bearing.time = [0, now, 0, 0],
                    value = bearing.value = [0, map.getBearing(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 40000);
                    value[i] = Math.random() * 360 - 180;
                }
                bearing.fn = createInterpolant(time, value);
            } else if (now >= bearing.time[2]) {
                bearing.time = bearing.time.slice(1).concat(bearing.time[3] + Math.random() * 10000 + 40000);
                bearing.value = bearing.value.slice(1).concat(Math.random() * 360 - 180);
                bearing.fn = createInterpolant(bearing.time, bearing.value);
            }
            if (!pitch.time || reset) {
                const time = pitch.time = [0, now, 0, 0],
                    value = pitch.value = [0, map.getPitch(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 20000);
                    value[i] = Math.random() * 30 + 45;
                }
                pitch.fn = createInterpolant(time, value);
            } else if (now >= pitch.time[2]) {
                pitch.time = pitch.time.slice(1).concat(pitch.time[3] + Math.random() * 10000 + 20000);
                pitch.value = pitch.value.slice(1).concat(Math.random() * 30 + 45);
                pitch.fn = createInterpolant(pitch.time, pitch.value);
            }
        } else {
            delete trackingParams.zoom.time;
            delete trackingParams.bearing.time;
            delete trackingParams.pitch.time;
            if (trackingMode === 'drone') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing - (t - now) / 200) % 360;
            } else if (trackingMode === 'helicopter') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing + (t - now) / 400) % 360;
            }
        }
    }

    updateHandlersAndControls() {
        const me = this,
            {map, trackedObject, trackingMode} = me,
            handlers = ['scrollZoom', 'boxZoom', 'dragRotate', 'dragPan', 'keyboard', 'doubleClickZoom', 'touchZoomRotate', 'touchPitch'];

        if (isVehicle(trackedObject) && trackingMode !== 'position') {
            for (const handler of handlers) {
                map[handler].disable();
            }
            me.navControl.disable();
        } else {
            for (const handler of handlers) {
                map[handler].enable();
            }
            if (isVehicle(trackedObject) && trackingMode === 'position') {
                map.dragPan.disable();
            }
            me.navControl.enable();
        }
    }

    startViewAnimation() {
        const me = this;
        let t2 = 0,
            t4 = 0;

        me.viewAnimationID = animation.start({
            callback: (elapsed, duration) => {
                const trackedObject = me.trackedObject,
                    t1 = easeOutQuart(elapsed / duration),
                    easeOutFactor = 1 - (1 - t1) / (1 - t2),
                    t3 = easeInQuad(elapsed / duration),
                    easeInFactor = 1 - (1 - t3) / (1 - t4);

                me._jumpTo({
                    center: trackedObject.coord,
                    altitude: trackedObject.altitude,
                    bearing: trackedObject.bearing,
                    easeOutFactor,
                    easeInFactor,
                    bearingFactor: easeOutFactor
                });
                t2 = t1;
                t4 = t3;
            },
            complete: () => {
                delete me.viewAnimationID;
            },
            duration: 1000
        });
    }

    stopViewAnimation() {
        const me = this;

        if (me.viewAnimationID) {
            animation.stop(me.viewAnimationID);
            delete me.viewAnimationID;
        }
    }

    /**
     * Returns a LngLat representing geographical coordinates on the ground
     * that shares the same projected pixel coordinates with the specified
     * geographical location and altitude.
     * @param {LngLatLike} coord - The geographical location to adjust
     * @param {number} altitude - The altitude in meters of the position
     * @param {number} bearing - The desired bearing of the camera in degrees.
     *     If not specified, the coordinates will be calculated using the
     *     current camera projection matrix
     * @returns {LngLat} The adjusted LngLat.
     */
    adjustCoord(coord, altitude, bearing) {
        if (!altitude) {
            return LngLat.convert(coord);
        }

        const {map, trafficLayer} = this;

        if (!isNaN(bearing)) {
            const mCoord = MercatorCoordinate.fromLngLat(coord, altitude),
                offset = mCoord.z * Math.tan(map.getPitch() * DEGREE_TO_RADIAN),
                x = mCoord.x + offset * Math.sin(bearing * DEGREE_TO_RADIAN),
                y = mCoord.y - offset * Math.cos(bearing * DEGREE_TO_RADIAN);

            return new MercatorCoordinate(x, y, 0).toLngLat();
        }
        return map.unproject(trafficLayer.project(coord, altitude));
    }

    getLocalizedRailwayTitle(railway) {
        const me = this,
            title = (railway || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedTrainNameOrRailwayTitle(names, railway) {
        const me = this;

        return names ?
            names.map(name => name[me.lang] || name.en).join(me.dict['and']) :
            me.getLocalizedRailwayTitle(railway);
    }

    getLocalizedTrainTypeTitle(type) {
        const me = this,
            title = (type || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedStationTitle(array) {
        const me = this,
            stations = Array.isArray(array) ? array : [array];

        return stations.map(station => {
            const title = (station || {}).title || {};
            return title[me.lang] || title.en;
        }).join(me.dict['and']);
    }

    getLocalizedRailDirectionTitle(direction) {
        const me = this,
            title = (direction || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedDestinationTitle(destination, direction) {
        const me = this;

        return destination ?
            me.dict['for'].replace('$1', me.getLocalizedStationTitle(destination)) :
            me.getLocalizedRailDirectionTitle(direction);
    }

    getLocalizedOperatorTitle(operator) {
        const me = this,
            title = (operator || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedAirportTitle(airport) {
        const me = this,
            title = (airport || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedFlightStatusTitle(status) {
        const me = this,
            title = (status || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOITitle(poi) {
        const me = this,
            title = (poi || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOIDescription(poi) {
        const me = this,
            description = (poi || {}).description || {};

        return description[me.lang] || description.en;
    }

    getTrainDescription(train) {
        const me = this,
            {lang, dict} = me,
            {r: railway, ad, departureTime, arrivalStation} = train,
            color = (ad && ad.color) || (train.v || railway).color,
            delay = train.delay || 0,
            arrivalTime = helpers.valueOrDefault(train.arrivalTime, train.nextDepartureTime),
            status = railway.status;

        return [
            '<div class="desc-header">',
            Array.isArray(color) ? [
                '<div>',
                ...color.slice(0, 3).map(c => `<div class="line-strip" style="background-color: ${c};"></div>`),
                '</div>'
            ].join('') : `<div style="background-color: ${color};"></div>`,
            '<div><strong>',
            me.getLocalizedTrainNameOrRailwayTitle(train.nm, railway),
            '</strong>',
            railway.operator ? `<br> <span class="train-type-label">${me.getLocalizedOperatorTitle(me.operators.get(railway.operator))}</span> ` : '<br>',
            me.getLocalizedDestinationTitle(train.ds, train.d),
            '</div></div>',
            `<strong>${dict['train-number']}:</strong> ${train.n}`,
            !train.timetable ? ` <span class="desc-caution">${dict['special']}</span>` : '',
            '<br>',
            ad ? `<span class="desc-ad-cars" style="color: ${ad.textcolor};">${ad.description[lang]}</span><br>` : '',
            delay >= 60000 ? '<span class="desc-caution">' : '',
            '<strong>',
            dict[train.standing ? 'standing-at' : 'previous-stop'],
            ':</strong> ',
            me.getLocalizedStationTitle(train.departureStation),
            departureTime !== undefined ? ` ${helpers.getTimeString(departureTime + delay)}` : '',
            arrivalStation ? [
                `<br><strong>${dict['next-stop']}:</strong> `,
                me.getLocalizedStationTitle(arrivalStation),
                arrivalTime !== undefined ? ` ${helpers.getTimeString(arrivalTime + delay)}` : ''
            ].join('') : '',
            delay >= 60000 ? `<br>${dict['delay'].replace('$1', Math.floor(delay / 60000))}</span>` : '',
            status && lang === 'ja' ? `<br><span class="desc-caution"><strong>${status}:</strong> ${railway.text}</span>` : ''
        ].join('');
    }

    getFlightDescription(flight) {
        const me = this,
            dict = me.dict,
            {a: airline, n: flightNumber, ds: destination} = flight,
            tailcolor = airline.tailcolor || '#FFFFFF',
            scheduledTime = helpers.valueOrDefault(flight.sdt, flight.sat),
            estimatedTime = helpers.valueOrDefault(flight.edt, flight.eat),
            actualTime = helpers.valueOrDefault(flight.adt, flight.aat),
            delayed = (estimatedTime !== undefined || actualTime !== undefined) && scheduledTime !== helpers.valueOrDefault(estimatedTime, actualTime);

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${tailcolor};"></div>`,
            `<div><strong>${me.getLocalizedOperatorTitle(airline)}</strong>`,
            `<br>${flightNumber[0]} `,
            dict[destination ? 'to' : 'from'].replace('$1', me.getLocalizedAirportTitle(destination || flight.or)),
            '</div></div>',
            `<strong>${dict['status']}:</strong> ${me.getLocalizedFlightStatusTitle(flight.s)}`,
            '<br><strong>',
            dict[destination ? 'scheduled-departure-time' : 'scheduled-arrival-time'],
            `:</strong> ${helpers.getTimeString(scheduledTime)}`,
            delayed ? '<span class="desc-caution">' : '',
            estimatedTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'estimated-departure-time' : 'estimated-arrival-time'],
                `:</strong> ${helpers.getTimeString(estimatedTime)}`
            ].join('') : actualTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'actual-departure-time' : 'actual-arrival-time'],
                `:</strong> ${helpers.getTimeString(actualTime)}`
            ].join('') : '',
            delayed ? '</span>' : '',
            flightNumber.length > 1 ? `<br><strong>${dict['code-share']}:</strong> ${flightNumber.slice(1).join(' ')}` : ''
        ].join('');
    }

    getBusDescription(bus) {
        const me = this,
            dict = me.dict,
            gtfs = me.gtfs.get(bus.gtfsId),
            stopLookup = gtfs.stopLookup,
            {route, headsigns, stops} = bus.trip,
            routeInfo = gtfs.routeLookup.get(route),
            {shortName, color, textColor} = routeInfo,
            operator = routeInfo.operator || gtfs.agency,
            operatorName = {'SBST': 'SBS Transit', 'TTS': 'Tower Transit', 'GAS': 'Go-Ahead Singapore', 'SMRT': 'SMRT Buses'}[operator] || operator,
            operatorColor = {'SBST': '#7B2D8B', 'TTS': '#006B3F', 'GAS': '#003D7C', 'SMRT': '#EE2E24'}[operator] || gtfs.color,
            labelStyle = [
                'color: #FFFFFF;',
                'background-color: #555555;'
            ].join(' '),
            nextStopIndex = bus.sectionIndex + (bus.standing ? 0 : bus.sectionLength),
            nextStopName = stopLookup.get(stops[nextStopIndex]).name,
            prevStopIndex = Math.max(0, nextStopIndex - 1),
            prevStopName = stopLookup.get(stops[prevStopIndex]).name;

        const loadIndicator = bus.load === 'LSD' ? '<span class="bus-load bus-load-lsd">&#x1F9CD;&#x1F9CD;&#x1F9CD;</span>' :
            bus.load === 'SDA' ? '<span class="bus-load bus-load-sda">&#x1F9CD;&#x1F9CD;</span>' :
            '<span class="bus-load bus-load-sea">&#x1F9CD;</span>';

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${operatorColor};"></div>`,
            `<div><strong>${operatorName}</strong>`,
            bus.stop !== undefined ? '<span class="realtime-small-icon"></span>' : '',
            '<br>',
            shortName ? `<span class="bus-route-label" style="${labelStyle}">Bus ${shortName}</span> ` : '',
            loadIndicator,
            '</div></div>',
            `<strong>${dict['previous-busstop']}:</strong> ${prevStopName}<br>`,
            `<strong>${dict['next-busstop']}:</strong> ${nextStopName}`
        ].join('');
    }

    /**
     * Check if any of connecting trains is active
     * @param {Object} train - train to check
     * @returns {boolean} True if any of connecting trains is active
     */
    checkActiveTrains(train) {
        const me = this;

        function check(curr, prop) {
            const activeTrain = me.activeTrainLookup.get(curr.t);

            // Need to check timetable ID
            if (activeTrain && curr.id === activeTrain.timetable.id) {
                return true;
            }

            const timetables = curr[prop];

            if (timetables) {
                for (const timetable of timetables) {
                    if (check(timetable, prop)) {
                        return true;
                    }
                }
            }
            return false;
        }

        return train.timetable ? check(train.timetable, 'pt') || check(train.timetable, 'nt') : false;
    }

    stopTrain(train) {
        const me = this;

        animation.stop(train.animationID);
        me.hideAdTrainPopup(train);
        if (train === me.markedObject) {
            me.markObject();
        }
        if (train === me.trackedObject) {
            me.trackObject();
        }
        me.activeTrainLookup.delete(train.id);
        return me.trafficLayer.removeObject(train);
    }

    stopFlight(flight) {
        const me = this;

        animation.stop(flight.animationID);
        if (flight === me.markedObject) {
            me.markObject();
        }
        if (flight === me.trackedObject) {
            me.trackObject();
        }
        me.activeFlightLookup.delete(flight.id);
        return me.trafficLayer.removeObject(flight);
    }

    stopBus(bus) {
        const me = this;

        animation.stop(bus.animationID);
        if (bus === me.markedObject) {
            me.markObject();
        }
        if (bus === me.trackedObject) {
            me.trackObject();
        }
        me.gtfs.get(bus.gtfsId).activeBusLookup.delete(bus.trip.id);
        return me.trafficLayer.removeObject(bus);
    }

    stopAll() {
        const me = this,
            promises = [];

        for (const train of me.activeTrainLookup.values()) {
            promises.push(me.stopTrain(train));
        }
        me.standbyTrainLookup.clear();
        me.realtimeTrains.clear();
        me.adTrains.clear();
        for (const flight of me.activeFlightLookup.values()) {
            promises.push(me.stopFlight(flight));
        }
        for (const {activeBusLookup, realtimeBuses} of me.gtfs.values()) {
            for (const bus of activeBusLookup.values()) {
                promises.push(me.stopBus(bus));
            }
            realtimeBuses.clear();
        }
        if (promises.length > 0) {
            me.removing = true;
            Promise.all(promises).then(() => {
                delete me.removing;
                delete me.lastRefresh;
                delete me.lastRealtimeCheck;
            });
        }
    }

    resetRailwayStatus() {
        for (const railway of this.railways.getAll()) {
            delete railway.status;
            delete railway.text;
            delete railway.suspended;
        }
    }

    refreshTrainTimetableData() {
        const me = this;

        showLoader(me.container);
        me.timetables.clear();
        loadTimetableData(me.dataUrl, me.clock).then(data => {
            me.timetables = new TrainTimetables(data, me.dataReferences);
            delete me.lastRefresh;
            delete me.lastRealtimeCheck;
            hideLoader(me.container);
        });
    }

    refreshBusData(replace) {
        const me = this,
            map = me.map,
            sourceIds = new Set(me.dataSources.map(s => s.gtfsUrl || s.type)),
            deleteGtfs = ({id, activeBusLookup, layerIds, routeGroupIndex, colorGroupIndex}) => {
                const styleOpacities = me.styleOpacities,
                    promises = [];

                for (const layerId of layerIds) {
                    me.removeLayer(layerId);
                    for (let i = 0, ilen = styleOpacities.length; i < ilen; i++) {
                        if (styleOpacities[i].id === layerId) {
                            styleOpacities.splice(i, 1);
                            break;
                        }
                    }
                }
                map.removeSource(id);
                for (const bus of activeBusLookup.values()) {
                    promises.push(me.stopBus(bus));
                }
                me.gtfs.delete(id);
                return Promise.all(promises).then(() => {
                    me.trafficLayer.removeRouteGroup(routeGroupIndex);
                    me.trafficLayer.removeColorGroup(colorGroupIndex);
                });
            };

        for (const [id, gtfs] of me.gtfs) {
            if (!sourceIds.has(id)) {
                deleteGtfs(gtfs);
            }
        }

        const loadPromises = [];

        for (const source of me.dataSources) {
            const id = source.gtfsUrl || source.type;

            if (me.gtfs.has(id) && !replace) {
                continue;
            }

            const loadPromise = source.type === 'lta'
                ? loadLtaBusData(configs.dataUrl, me.clock)
                : loadBusData(source, me.clock, me.lang);

            loadPromises.push(loadPromise.then(data =>
                me.initialized ? data : new Promise(resolve => me.once('initialized', () => resolve(data)))
            ).then(data =>
                me.gtfs.has(id) ? deleteGtfs(me.gtfs.get(id)).then(() => data) : data
            ).then(data => {
                const {agency, featureCollection, version} = data,
                    featureLookup = new Map(),
                    stopLookup = new Map(),
                    routeLookup = new Map(),
                    tripLookup = new Map(),
                    layerIds = new Set(),
                    routeData = [];

                featureEach(featureCollection, feature => {
                    const properties = feature.properties;

                    if (properties.type === 0) {
                        featureLookup.set(properties.id, feature);
                        routeData.push({id: `${id}.${properties.id}`, feature});
                    }
                });
                for (const stop of data.stops) {
                    stopLookup.set(stop.id, stop);
                }
                for (const route of data.routes) {
                    routeLookup.set(route.id, route);
                }
                for (const trip of data.trips) {
                    tripLookup.set(trip.id, trip);
                }
                me.gtfs.set(id, {
                    id,
                    agency,
                    version,
                    featureLookup,
                    stopLookup,
                    routeLookup,
                    tripLookup,
                    layerIds,
                    activeBusLookup: new Map(),
                    realtimeBuses: new Set(),
                    vehiclePositionUrl: source.vehiclePositionUrl,
                    color: source.color,
                    routeGroupIndex: me.trafficLayer.addRouteGroup(routeData),
                    colorGroupIndex: me.trafficLayer.addColorGroup([
                        {id, color: ['#E8E8E8', '#FFFFFF', '#CC0000', '#222222']},
                        {id: `${id}.SBST`, color: ['#7B2D8B', '#FFFFFF', '#CC0000', '#222222']},
                        {id: `${id}.TTS`, color: ['#006B3F', '#FFFFFF', '#006B3F', '#222222']},
                        {id: `${id}.GAS`, color: ['#003D7C', '#FFFFFF', '#003D7C', '#222222']},
                        {id: `${id}.SMRT`, color: ['#EE2E24', '#FFFFFF', '#EE2E24', '#222222']}
                    ])
                });

                map.addSource(id, {
                    type: 'geojson',
                    data: featureCollection,
                    promoteId: 'id'
                });

                for (const key of ['busroute', 'busroute-highlighted']) {
                    me.addLayer({
                        id: `${key}-${id}-og-`,
                        type: 'line',
                        source: id,
                        filter: ['==', ['get', 'type'], 0],
                        paint: {
                            'line-color': {
                                'busroute': ['get', 'color'],
                                'busroute-highlighted': '#FFFFFF'
                            }[key],
                            'line-opacity': {
                                'busroute': 0,
                                'busroute-highlighted': [
                                    'case',
                                    [
                                        'all',
                                        [
                                            'any',
                                            ['to-boolean', ['feature-state', 'route-highlight']],
                                            ['to-boolean', ['feature-state', 'trip-highlight']],
                                        ],
                                        ['!', ['to-boolean', ['feature-state', 'hidden']]]
                                    ],
                                    0.7,
                                    0
                                ]
                            }[key],
                            'line-width': {
                                'busroute': ['interpolate', ['exponential', 2], ['zoom'], 11, 1, 14, 2, 19, 4, 22, 16],
                                'busroute-highlighted': ['interpolate', ['linear'], ['zoom'], 12, 2, 16, 3, 20, 5]
                            }[key],
                            'line-blur': {
                                'busroute': 0,
                                'busroute-highlighted': 2
                            }[key],
                            'line-emissive-strength': 1
                        },
                        metadata: {
                            'busroute': {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-route': 0.3,
                                'mt3d:opacity-underground': 0,
                                'mt3d:opacity-underground-route': 0
                            },
                            'busroute-highlighted': {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-route': 0.3,
                                'mt3d:opacity-underground': 0.25,
                                'mt3d:opacity-underground-route': 0.3
                            }
                        }[key]
                    }, 'railways-og-13');
                    layerIds.add(`${key}-${id}-og-`);
                }

                // Bus stops as pin markers — single layer, always visible
                // Only shown when route is highlighted (stop-highlight state)
                map.addLayer({
                    id: `busstops-${id}-og`,
                    type: 'circle',
                    source: id,
                    filter: ['==', ['get', 'type'], 1],
                    paint: {
                        'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 4, 16, 5, 18, 7, 22, 14],
                        'circle-color': '#FFFFFF',
                        'circle-opacity': [
                            'case',
                            ['to-boolean', ['feature-state', 'stop-highlight']],
                            0.9,
                            0
                        ],
                        'circle-stroke-width': 2,
                        'circle-stroke-color': [
                            'case',
                            ['==', ['feature-state', 'operator-idx'], 1], '#7B2D8B',
                            ['==', ['feature-state', 'operator-idx'], 2], '#006B3F',
                            ['==', ['feature-state', 'operator-idx'], 3], '#003D7C',
                            ['==', ['feature-state', 'operator-idx'], 4], '#EE2E24',
                            '#4CAF50'
                        ],
                        'circle-stroke-opacity': [
                            'case',
                            ['to-boolean', ['feature-state', 'stop-highlight']],
                            0.9,
                            0
                        ],
                        'circle-pitch-alignment': 'map',
                        'circle-emissive-strength': 1
                    }
                }, 'railways-og-13');
                layerIds.add(`busstops-${id}-og`);

                // Next stop pulse glow layer (animated via render loop)
                map.addLayer({
                    id: `busstops-pulse-${id}-og`,
                    type: 'circle',
                    source: id,
                    filter: ['==', ['get', 'type'], 1],
                    paint: {
                        'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 8, 16, 12, 18, 16, 22, 24],
                        'circle-color': [
                            'case',
                            ['==', ['feature-state', 'operator-idx'], 1], '#7B2D8B',
                            ['==', ['feature-state', 'operator-idx'], 2], '#006B3F',
                            ['==', ['feature-state', 'operator-idx'], 3], '#003D7C',
                            ['==', ['feature-state', 'operator-idx'], 4], '#EE2E24',
                            '#4CAF50'
                        ],
                        'circle-opacity': [
                            'case',
                            ['to-boolean', ['feature-state', 'next-stop']],
                            0.4,
                            0
                        ],
                        'circle-blur': 1,
                        'circle-pitch-alignment': 'map',
                        'circle-emissive-strength': 1
                    }
                }, `busstops-${id}-og`);
                layerIds.add(`busstops-pulse-${id}-og`);

                me.addLayer({
                    id: `busstops-poi-${id}`,
                    type: 'symbol',
                    source: id,
                    filter: ['==', ['get', 'type'], 2],
                    layout: {
                        'text-field': ['get', 'name'],
                        'text-font': [
                            'Open Sans Bold',
                            'Arial Unicode MS Bold'
                        ],
                        'text-max-width': 9,
                        'text-padding': 2,
                        'text-size': 12,
                        'text-anchor': 'bottom',
                        'text-offset': [0, -1]
                    },
                    paint: {
                        'text-color': 'rgba(102,102,102,1)',
                        'text-halo-blur': 0.5,
                        'text-halo-color': 'rgba(255,255,255,1)',
                        'text-halo-width': 1
                    },
                    minzoom: 14
                });
                layerIds.add(`busstops-poi-${id}`);

                const styleOpacities = me.styleOpacities,
                    existingLayerIds = new Set(styleOpacities.map(({id}) => id));

                for (const item of helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect')) {
                    if (!existingLayerIds.has(item.id)) {
                        styleOpacities.push(item);
                    }
                }
                me.refreshMap();

                if (me.searchMode === 'none') {
                    if (me.clockMode === 'playback') {
                        me.refreshBuses(id);
                    } else if (me.clockMode === 'realtime') {
                        me.refreshRealtimeBusData(id);
                    }
                }
            }));
        }

        me.updateBusRouteVisibility();

        // Start real-time bus arrival poller (only once bus lines are enabled)
        if (me.busLinesEnabled && !me.busArrivalPoller) {
            me.busArrivalPoller = new BusArrivalPoller(me);
            me.busArrivalPoller.onUpdate(() => {
                const gtfs = me.gtfs.get('lta');
                if (!gtfs) return;
                for (const bus of gtfs.activeBusLookup.values()) {
                    const serviceNo = gtfs.routeLookup.get(bus.trip.route)?.shortName;
                    if (serviceNo) {
                        const load = me.busArrivalPoller.getLoadForService(serviceNo);
                        if (load) {
                            bus.load = load;
                        }
                    }
                }
                me.aboutPanel.updateContent();
            });
            me.busArrivalPoller.start();
        }

        return Promise.all(loadPromises);
    }

    refreshRealtimeTrainData() {
        const me = this;

        loadDynamicTrainData(me.secrets).then(({trainData, trainInfoData}) => {
            const {activeTrainLookup, standbyTrainLookup, realtimeTrains, dataReferences} = me,
                now = me.clock.getTimeOffset();

            me.resetRailwayStatus();

            for (const trainInfoRef of trainInfoData) {
                const railway = me.railways.get(trainInfoRef.railway),
                    status = trainInfoRef.status;

                // Train information text is provided in Japanese only
                if (railway && status && status.ja) {
                    railway.status = status.ja;
                    railway.text = trainInfoRef.text.ja;
                }

                if (trainInfoRef.suspended) {
                    railway.suspended = true;
                }
            }

            standbyTrainLookup.clear();
            realtimeTrains.clear();

            for (const trainRef of trainData) {
                const {id, r, n, y, d, os, ds, ts, fs, v, ad, delay, carComposition} = trainRef,
                    aliasId = id.replace('.Marunouchi.', '.MarunouchiBranch.');

                me.lastDynamicUpdate[trainRef.o] = trainRef.date;
                realtimeTrains.add(trainRef.id);

                // Retry lookup replacing Marunouchi line with MarunouchiBranch line
                const activeTrain = activeTrainLookup.get(id) || activeTrainLookup.get(aliasId);

                let marked, tracked;

                // Update the avtive train if exists
                if (activeTrain) {
                    if ((y && y !== activeTrain.y.id) ||
                        (os && activeTrain.os && os[0] !== activeTrain.os[0].id) ||
                        (ds && activeTrain.ds && ds[0] !== activeTrain.ds[0].id) ||
                        (v && v !== (activeTrain.v || {}).id) ||
                        (ad && !activeTrain.ad) ||
                        (!isNaN(carComposition) && carComposition !== activeTrain.carComposition) ||
                        (!isNaN(delay) && delay !== activeTrain.delay)) {
                        marked = me.markedObject === activeTrain;
                        tracked = me.trackedObject === activeTrain;
                        me.stopTrain(activeTrain);
                    } else {
                        if (!activeTrain.timetable) {
                            activeTrain.update({ts, fs}, dataReferences);
                        }
                        continue;
                    }
                }

                let timetables = me.timetables.getByTrainId(id);

                // Retry lookup replacing Marunouchi line with MarunouchiBranch line
                if (timetables.length === 0) {
                    timetables = me.timetables.getByTrainId(aliasId);
                }

                // Start train with timetable
                if (timetables.length !== 0) {
                    for (const timetable of timetables) {
                        const train = new Train(timetable);

                        train.update({y, os, ds, v, ad, delay, carComposition}, dataReferences);
                        if (timetable.start + (delay || 0) <= now && now <= timetable.end + (delay || 0)) {
                            me.trainStart(train, {marked, tracked});
                        } else {
                            standbyTrainLookup.set(timetable.id, train);
                        }
                    }
                    continue;
                }

                if (!r) {
                    continue;
                }

                // Start train without timetable
                me.trainStart(new Train({id, r, n, y, d, os, ds, ts, fs, delay, carComposition}, dataReferences));
            }

            // Stop trains if they are no longer active
            for (const train of activeTrainLookup.values()) {
                const railway = train.r;

                if ((((railway.status && railway.dynamic) || !train.timetable) && !realtimeTrains.has(train.id)) || railway.suspended) {
                    me.stopTrain(train);
                }
            }

            me.refreshTrains();
            me.aboutPanel.updateContent();
            if (me.debugCountsDiv) {
                me._updateDebugCounts();
            }
        }).catch(error => {
            me.refreshTrains();
            console.log(error);
        });
    }

    _updateDebugCounts() {
        const me = this;
        if (!me.debugCountsDiv) return;
        const activeBy = new Map();
        const standbyBy = new Map();
        for (const tr of me.activeTrainLookup.values()) {
            const id = tr.r.id; activeBy.set(id, (activeBy.get(id) || 0) + 1);
        }
        for (const tr of me.standbyTrainLookup.values()) {
            const id = tr.r.id; standbyBy.set(id, (standbyBy.get(id) || 0) + 1);
        }
        const rows = [];
        for (const rw of me.railways.getAll()) {
            const id = rw.id; const title = (rw.title && (rw.title[me.lang] || rw.title.en)) || id;
            const a = activeBy.get(id) || 0; const s = standbyBy.get(id) || 0;
            rows.push(`${title}: ${a} active, ${s} standby`);
        }
        me.debugCountsDiv.textContent = rows.join('\n');
    }

    refreshRealtimeFlightData() {
        const me = this;

        loadDynamicFlightData(me.secrets).then(({atisData, flightData}) => {
            // Skip if no flight data available
            if (!atisData || !atisData.landing || !flightData || flightData.length === 0) {
                return;
            }

            const flightLookup = me.flightLookup,
                flightQueue = {};

            // Changi Airport runway configuration
            // Arrivals: 20R/20C (from NNE), Departures: 02L/02C (to NNE)
            const arrRoutes = {S: '20C', N: '20R'},
                depRoutes = {S: '02C', N: '02L'};

            for (const flightRef of flightData) {
                const {id, n, dp, ds, ar, or, sat, sdt} = flightRef;
                let flight = flightLookup.get(id),
                    status = flightRef.s,
                    {maxFlightSpeed: maxSpeed, flightAcceleration: acceleration} = configs;

                if (!flight) {
                    if (helpers.includes(['Cancelled'], status)) {
                        continue;
                    }
                    const airport = me.airports.get(ds || or),
                        direction = airport ? airport.direction : 'N',
                        route = dp === 'WSSS' ? `WSSS.${depRoutes[direction]}.Dep` :
                        ar === 'WSSS' ? `WSSS.${arrRoutes[direction]}.Arr` : undefined,
                        feature = me.featureLookup.get(route);

                    if (feature) {
                        flight = new Flight({
                            id,
                            n,
                            a: flightRef.a,
                            dp,
                            ar,
                            ds,
                            or
                        }, {
                            airports: me.airports,
                            operators: me.operators
                        });
                        flight.runway = route.replace(/^([^.]+\.)[A-Z]*([^.]+).+/, '$1$2');
                        flight.feature = feature;
                        flightLookup.set(flight.id, flight);
                    } else {
                        continue;
                    }
                }
                flight.update({
                    edt: flightRef.edt,
                    adt: flightRef.adt,
                    sdt,
                    eat: flightRef.eat,
                    aat: flightRef.aat,
                    sat
                });

                const departureTime = helpers.valueOrDefault(flight.edt, helpers.valueOrDefault(flight.adt, flight.sdt)),
                    arrivalTime = helpers.valueOrDefault(flight.eat, helpers.valueOrDefault(flight.aat, flight.sat));

                if (arrivalTime !== undefined && !status) {
                    status = 'InFlight';
                } else if (departureTime !== undefined && !status) {
                    status = 'Departed';
                }
                flight.update({s: status}, {flightStatuses: me.flightStatuses});

                if (arrivalTime !== undefined) {
                    maxSpeed /= 2;
                    acceleration /= -2;
                }

                const duration = maxSpeed / Math.abs(acceleration) / 2 + flight.feature.properties.length / maxSpeed,
                    standingDuration = configs.standingDuration;

                if (departureTime) {
                    flight.start = flight.base = departureTime;
                    flight.entry = flight.start - standingDuration;
                    flight.end = flight.start + duration;
                } else {
                    flight.start = flight.entry = arrivalTime - duration;
                    flight.base = flight.start + duration - standingDuration;
                    flight.end = flight.start + duration + standingDuration;
                }
                flight.maxSpeed = maxSpeed;
                flight.acceleration = acceleration;

                const queue = flightQueue[flight.runway] = flightQueue[flight.runway] || [];
                queue.push(flight);

                me.lastDynamicUpdate[flightRef.o] = flightRef.date;
            }

            for (const key of Object.keys(flightQueue)) {
                const queue = flightQueue[key];
                let latest = 0;

                queue.sort((a, b) => a.base - b.base);
                for (const flight of queue) {
                    const base = flight.base,
                        delay = Math.max(base, latest + configs.minFlightInterval) - base;

                    if (delay) {
                        flight.start += delay;
                        flight.base += delay;
                        flight.entry += delay;
                        flight.end += delay;
                    }
                    latest = flight.base;
                }
            }

            me.refreshFlights();
            me.lastFlightUpdate = Date.now();
            me.aboutPanel.updateContent();
        }).catch(error => {
            me.refreshFlights();
            console.log(error);
        });
    }

    refreshRealtimeBusData(gtfsId) {
        const me = this;

        for (const gtfs of gtfsId ? [me.gtfs.get(gtfsId)] : me.gtfs.values()) {
            const {id: gtfsId, vehiclePositionUrl, featureLookup, stopLookup, routeLookup, tripLookup, activeBusLookup, realtimeBuses} = gtfs;

            if (!vehiclePositionUrl) {
                me.refreshBuses(gtfsId);
                continue;
            }

            loadDynamicBusData(vehiclePositionUrl).then(data => {
                realtimeBuses.clear();
                gtfs.date = me.clock.getString(data.header.timestamp * 1000);

                for (const {id, vehicle: vp} of data.entity) {
                    if (!vp) {
                        continue;
                    }

                    const vehicle = vp.vehicle,
                        stop = vp.currentStopSequence,
                        position = vp.position,
                        tripId = vp.trip && vp.trip.tripId;

                    if (!(stop || position) || !tripId) {
                        continue;
                    }

                    realtimeBuses.add(tripId);

                    const busTrip = tripLookup.get(tripId),
                        feature = busTrip && featureLookup.get(busTrip.shape),
                        route = busTrip && routeLookup.get(busTrip.route);

                    if (!busTrip || !feature || !route || route.hidden) {
                        continue;
                    }

                    const isActive = activeBusLookup.has(tripId);
                    let bus;

                    if (isActive) {
                        bus = activeBusLookup.get(tripId);
                    } else {
                        let offset = 0;
                        const offsets = busTrip.stops.map(stopId =>
                            // Use the previous offset to calulate a weight and pick a closer point
                            (offset = nearestCloserPointOnLine(feature, stopLookup.get(stopId).coord, offset).properties.location)
                        );

                        bus = {
                            type: 'bus',
                            id: vehicle ? vehicle.licensePlate || vehicle.id : id,
                            gtfsId,
                            trip: busTrip,
                            feature,
                            offsets,
                            offset: 0
                        };
                    }
                    if (stop) {
                        bus.stop = stop;
                    } else {
                        const offsets = bus.offsets,
                            // Use the current bus.offset to calulate a weight and pick a closer point
                            offset = nearestCloserPointOnLine(feature, [position.longitude, position.latitude], bus.offset).properties.location;

                        bus.stop = busTrip.stopSequences[offsets.reduce(
                            (acc, cur, i) => cur < offset ? Math.min(i + 1, offsets.length - 1) : acc, 0
                        )];
                    }
                    if (!isActive) {
                        me.busStart(bus);
                    }
                }

                me.aboutPanel.updateContent();
            }).catch(error => {
                console.log(error);
            });
        }
    }

    updateUndergroundButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-underground');

        if (button) {
            const classList = button.classList;

            if (mode === 'underground') {
                button.title = dict['exit-underground'];
                classList.add('mapboxgl-ctrl-underground-visible');
            } else {
                button.title = dict['enter-underground'];
                classList.remove('mapboxgl-ctrl-underground-visible');
            }
        }
    }

    updatePlaybackButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-playback');

        if (button) {
            const classList = button.classList;

            if (mode === 'playback') {
                button.title = dict['exit-playback'];
                classList.add('mapboxgl-ctrl-playback-active');
            } else {
                button.title = dict['enter-playback'];
                classList.remove('mapboxgl-ctrl-playback-active');
            }
        }
    }

    updateEcoButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-eco');

        if (button) {
            const classList = button.classList;

            if (mode === 'eco') {
                button.title = dict['exit-eco'];
                classList.add('mapboxgl-ctrl-eco-active');
            } else {
                button.title = dict['enter-eco'];
                classList.remove('mapboxgl-ctrl-eco-active');
            }
        }
    }

    refreshMap() {
        const me = this,
            {viewMode, searchMode} = me,
            isUndergroundMode = viewMode === 'underground',
            isNotSearchResultMode = searchMode === 'none' || searchMode === 'edit',
            factorKey = `mt3d:opacity${isUndergroundMode ? '-underground' : ''}`;

        helpersMapbox.setStyleOpacities(me.map, me.styleOpacities, isNotSearchResultMode ? factorKey : [`${factorKey}-route`, factorKey]);
        me.trafficLayer.setMode(viewMode, searchMode);
    }

    _setSearchMode(mode) {
        const me = this;

        if (me.searchMode !== mode) {
            me.searchMode = mode;
            me.stopAll();
            for (const plugin of me.plugins) {
                plugin.setVisibility(mode === 'none');
            }
            me.refreshMap();
        }
    }

    _setViewMode(mode) {
        const me = this;

        if (me.viewMode === mode) {
            return;
        }

        me.updateUndergroundButton(mode);
        me.viewMode = mode;
        me.refreshMap();
        me.fire({type: 'viewmode', mode});
    }

    _setTrackingMode(mode) {
        const me = this;

        if (me.trackingMode === mode) {
            return;
        }

        me.trackingMode = mode;
        if (isVehicle(me.trackedObject)) {
            me.updateBaseZoom();
            me.updateTrackingParams(true);
            me.updateHandlersAndControls();
            me.startViewAnimation();
        }
        me.fire({type: 'trackingmode', mode});
    }

    _setClockMode(mode) {
        const me = this;

        if (me.clockMode === mode) {
            return;
        }

        me.updatePlaybackButton(mode);
        me.clockMode = mode;
        me.clock.reset();
        me.onClockChange();
        if (me.clockControl) {
            me.clockCtrl.setMode(mode);
        }
        if (mode === 'playback') {
            me.resetRailwayStatus();
        }
        me.fire({type: 'clockmode', mode});
    }

    _setEcoMode(mode) {
        const me = this;

        if (me.ecoMode === mode) {
            return;
        }

        me.updateEcoButton(mode);
        me.ecoMode = mode;
        me.fire({type: 'ecomode', mode});
    }

    onClockChange() {
        const me = this,
            baseTime = me.clock.getTime('03:00');

        me.stopAll();
        me.markObject();
        me.trackObject();

        if (me.lastTimetableRefresh !== baseTime) {
            me.refreshTrainTimetableData();
            me.lastTimetableRefresh = baseTime;
        }
    }

    /**
     * Returns the light color based on the current date and time.
     * In the playback mode, the time in the simulation clock is used.
     * @returns {Object} Color object
     */
    getLightColor() {
        return helpersMapbox.getSunlightColor(this.map, this.clock.getTime());
    }

    pickObject(point) {
        const me = this,
            {map, layerZoom} = me,
            modes = ['ground', 'underground'];
        let object;

        if (me.viewMode === 'underground') {
            modes.reverse();
        }
        for (const mode of modes) {
            object = me.trafficLayer.pickObject(mode, point);
            if (object) {
                return object;
            }
            if (mode === 'ground') {
                object = map.queryRenderedFeatures(point, {layers: [`stations-og-${layerZoom}`]})[0];
            } else {
                object = pickObject(map, `stations-ug-${layerZoom}`, point);
            }
            if (object) {
                return me.stationGroupLookup.get(object.properties.group);
            }
        }
    }

    markObject(object) {
        const me = this,
            {markedObject, trafficLayer, map, popup} = me;

        if (isEqualObject(markedObject, object)) {
            return;
        }

        if (markedObject) {
            delete me.markedObject;
            if (isVehicle(markedObject)) {
                trafficLayer.markObject();
                if (markedObject.type === 'bus') {
                    me.updateBusTripHighlight(markedObject);
                }
            } else {
                me.removeStationOutline('stations-marked');
            }
            if (popup && popup.isOpen()) {
                map.getCanvas().style.cursor = '';
                popup.remove();
            }
        }

        if (object) {
            me.markedObject = object;
            if (isVehicle(object)) {
                me.updateObjectPosition(object);
                trafficLayer.markObject(object);
                if (object.type === 'bus') {
                    me.updateBusTripHighlight(object);
                }
            } else {
                me.addStationOutline(object, 'stations-marked');
            }

            map.getCanvas().style.cursor = 'pointer';
            me.popup = new AnimatedPopup({
                className: helpers.isTouchDevice() ? 'popup-object popup-touch' : 'popup-object',
                closeButton: false,
                closeOnClick: false,
                maxWidth: '300px',
                offset: {
                    top: [0, 10],
                    bottom: [0, -30]
                },
                openingAnimation: {
                    duration: 300,
                    easing: 'easeOutBack'
                }
            });
            me.updatePopup({setHTML: true, addToMap: true});
        }
        me.updateAdTrainPopup();
        map.triggerRepaint();
    }

    trackObject(object) {
        const me = this,
            {searchMode, lang, map, trackedObject, trafficLayer, lastCameraParams, sharePanel, detailPanel} = me;

        if (searchMode === 'edit' && detailPanel && isStation(object)) {
            const {title, utitle} = object.stations[0],
                stationTitle = (utitle && utitle[lang]) || helpers.normalize(title[lang] || title.en);

            detailPanel.fillStationName(stationTitle);
            return;
        }

        if (isEqualObject(trackedObject, object)) {
            if ((isVehicle(object) || isStation(object)) && detailPanel) {
                detailPanel.reset();
            }
            return;
        }

        // Remember the camera params to restore them after the object is deselected
        if (!trackedObject && object) {
            lastCameraParams.viewMode = me.getViewMode();
            delete lastCameraParams.center;
            lastCameraParams.zoom = map.getZoom();
            lastCameraParams.bearing = map.getBearing();
            lastCameraParams.pitch = map.getPitch();
        } else if (trackedObject && !object) {
            me._setViewMode(lastCameraParams.viewMode);
            map.flyTo({
                center: lastCameraParams.center || map.getCenter(),
                zoom: lastCameraParams.zoom,
                bearing: lastCameraParams.bearing,
                pitch: lastCameraParams.pitch
            });
        }

        if (trackedObject) {
            delete me.trackedObject;
            if (isVehicle(trackedObject)) {
                trafficLayer.trackObject();
                if (trackedObject.type === 'bus') {
                    me.updateBusTripHighlight(trackedObject);
                }
                me.fire({type: 'deselection', deselection: trackedObject.id});
            } else if (isStation(trackedObject)) {
                me.removeStationOutline('stations-selected');
                me.fire({type: 'deselection', deselection: trackedObject.stations.map(({id}) => id)});
                me._setSearchMode('none');
                me.hideStationExits();
            } else {
                me.fire(Object.assign({type: 'deselection'}, trackedObject));
            }
            me.updateHandlersAndControls();
            me.stopViewAnimation();
            if (sharePanel) {
                sharePanel.remove();
                delete me.sharePanel;
            }
            if (detailPanel) {
                detailPanel.remove();
                delete me.detailPanel;
            }
        }

        if (object) {
            me.trackedObject = object;

            if (isVehicle(object)) {
                me.updateObjectPosition(object);
                me.updateBaseZoom();
                me.updateTrackingParams(true);
                me.updateHandlersAndControls();
                me.startViewAnimation();
                me._setViewMode(object.altitude < 0 ? 'underground' : 'ground');

                if (helpers.includes(['train', 'flight'], object.type) && me.clockMode === 'realtime' && navigator.share) {
                    me.sharePanel = new SharePanel({object});
                    me.sharePanel.addTo(me);
                }
                if (object.timetable) {
                    me.detailPanel = new TrainPanel({object});
                    me.detailPanel.addTo(me);
                } else if (object.trip) {
                    me.detailPanel = new BusPanel({object});
                    me.detailPanel.addTo(me);
                }

                trafficLayer.trackObject(object);
                if (object.type === 'bus') {
                    me.updateBusTripHighlight(object);
                }
                me.fire({type: 'selection', selection: object.id});
            } else if (isStation(object)) {
                const stations = object.stations.concat(object.hidden || []),
                    coords = stations.map(station => station.coord),
                    center = lastCameraParams.center = helpersMapbox.getBounds(coords).getCenter();

                if (object.layer === 'ground') {
                    me._setViewMode('ground');
                } else if (!map.getCenter().distanceTo(center)) {
                    me._setViewMode('underground');
                } else {
                    let prevZoom = map.getZoom();
                    const initialZoom = prevZoom,
                        onZoom = () => {
                            const zoom = map.getZoom();

                            if (zoom < prevZoom && zoom < initialZoom - 0.5) {
                                me._setViewMode('ground');
                            } else if (zoom > prevZoom && zoom > 15) {
                                me._setViewMode('underground');
                            }
                            prevZoom = zoom;
                        };

                    map.on('zoom', onZoom);
                    map.once('zoomend', () => {
                        me._setViewMode('underground');
                        map.off('zoom', onZoom);
                    });
                }
                map.flyTo({center, zoom: 15.5});

                me.detailPanel = new StationPanel({object: stations});
                me.detailPanel.addTo(me);

                me.addStationOutline(object, 'stations-selected');
                me.fire({type: 'selection', selection: object.stations.map(({id}) => id)});
            } else {
                me.fire(Object.assign({type: 'selection'}, object));
            }
        }
    }

    updateObjectPosition(object) {
        const {altitude: prevAltitude, standing: prevStanding} = object,
            {coord, altitude, bearing, _t} = this.trafficLayer.getObjectPosition(object);
        let viewMode, standing;

        object.coord = coord;
        object.altitude = altitude;
        object.bearing = bearing;
        object._t = _t;
        object.standing = _t === 0 || _t === 1;
        if (prevAltitude < 0 && altitude >= 0) {
            viewMode = 'ground';
        } else if (prevAltitude >= 0 && altitude < 0) {
            viewMode = 'underground';
        }
        if (prevStanding && !object.standing) {
            // Set only when starting
            standing = object.standing;
        }
        return {viewMode, standing};
    }

    showAdTrainPopup(train) {
        const me = this,
            adTrains = me.adTrains;

        if (adTrains.has(train)) {
            return;
        }

        const ad = train.ad;

        train.popup = new AnimatedPopup({
            className: 'popup-ad-cars',
            closeButton: false,
            closeOnClick: false,
            offset: {
                top: [0, 10],
                bottom: [0, -30]
            }
        });
        train.popup.setHTML(`<span style="color: ${ad.textcolor};">${ad.title[me.lang]}</span>`);
        adTrains.add(train);
        me.updateObjectPosition(train);
        me.updateAdTrainPopup();
    }

    updateAdTrainPopup() {
        const me = this,
            markedObject = me.markedObject;

        for (const train of me.adTrains) {
            train.popup.setLngLat(me.adjustCoord(train.coord, train.altitude));
            if (train !== markedObject && !train.popupVisible) {
                train.popup.addTo(me.map);
                train.popupVisible = true;
            } else if (train === markedObject && train.popupVisible) {
                train.popup.remove();
                delete train.popupVisible;
            }
        }
    }

    hideAdTrainPopup(train) {
        const me = this,
            adTrains = me.adTrains;

        if (!adTrains.has(train) || !train.popup) {
            return;
        }
        train.popup.remove();
        delete train.popup;
        delete train.popupVisible;
        adTrains.delete(train);
    }

    showStationExits(stations) {
        const me = this,
            map = me.map,
            exits = [].concat(...stations.map(station => station.exit || []));

        if (exits.length > 0) {
            const coords = [];

            me.exitPopups = exits.map((poi, index) => {
                const {coord, facilities = []} = poi,
                    icons = facilities.map(facility => `<span class="exit-${facility}-small-icon"></span>`).join(''),
                    listener = () => {
                        me.exitPopups[index] = setTimeout(() => {
                            const popup = new AnimatedPopup({
                                className: 'popup-exit',
                                closeButton: false,
                                closeOnClick: false
                            });

                            popup.setLngLat(coord)
                                .setHTML(icons + me.getLocalizedPOITitle(poi))
                                .addTo(map)
                                .getElement().id = `exit-${index}`;

                            me.exitPopups[index] = popup;
                        }, index / exits.length * 1000);
                    };

                map.once('moveend', listener);
                coords.push(coord);

                return listener;
            });

            map.fitBounds(helpersMapbox.getBounds(coords), {
                bearing: map.getBearing(),
                pitch: map.getPitch(),
                offset: [0, -map.transform.height / 12],
                padding: {top: 20, bottom: 20, left: 10, right: 50},
                maxZoom: 18
            });
        }
    }

    hideStationExits() {
        const me = this;

        for (const popup of me.exitPopups) {
            if (popup instanceof AnimatedPopup) {
                popup.remove();
            } else if (typeof popup === 'function') {
                me.map.off('moveend', popup);
            } else {
                clearTimeout(popup);
            }
        }
        me.exitPopups = [];
    }

    updateBaseZoom() {
        const me = this,
            {map, trackedObject: object} = me;

        if (isVehicle(object)) {
            const objectZ = me.getModelPosition(object.coord, object.altitude).z,
                cameraZ = map.getFreeCameraOptions().position.z;

            me.baseZoom = map.getZoom() + Math.log2(cameraZ / Math.abs(cameraZ - objectZ));
        }
    }

    updatePopup(options) {
        const me = this,
            {markedObject, map, popup} = me,
            {setHTML, addToMap} = options || {};

        if (isVehicle(markedObject)) {
            const bearing = markedObject === me.trackedObject ? map.getBearing() : undefined;

            popup.setLngLat(me.adjustCoord(markedObject.coord, markedObject.altitude, bearing));
            if (setHTML) {
                popup.setHTML(
                    markedObject.type === 'train' ? me.getTrainDescription(markedObject) :
                    markedObject.type === 'flight' ? me.getFlightDescription(markedObject) :
                    me.getBusDescription(markedObject)
                );
            }
        } else {
            const object = me.featureLookup.get(`${markedObject.id}.${me.layerZoom}`),
                coord = helpersGeojson.getCenterCoord(object),
                altitude = helpersGeojson.getAltitude(object);

            popup.setLngLat(me.adjustCoord(coord, altitude));
            if (setHTML) {
                const stations = markedObject.stations,
                    railwayColors = {},
                    stationCodesByTitle = {};

                for (const station of stations) {
                    const title = me.getLocalizedStationTitle(station),
                        railway = station.railway,
                        colors = railwayColors[title] = railwayColors[title] || {};

                    colors[me.getLocalizedRailwayTitle(railway)] = railway.color;

                    // Collect station codes
                    if (!stationCodesByTitle[title]) {
                        stationCodesByTitle[title] = [];
                    }
                    if (station.code) {
                        stationCodesByTitle[title].push({
                            code: station.code,
                            color: railway.color
                        });
                    }
                }
                popup.setHTML([
                    stations[0].thumbnail ? [
                        '<div class="thumbnail-image-container">',
                        '<div class="ball-pulse"><div></div><div></div><div></div></div>',
                        `<div class="thumbnail-image" style="background-image: url(\'${stations[0].thumbnail}\');"></div>`,
                        '</div>'
                    ].join('') : '',
                    '<div class="railway-list">',
                    Object.keys(railwayColors).map(stationTitle => {
                        // Build code badges
                        const codes = stationCodesByTitle[stationTitle] || [];
                        const codeBadges = codes.length > 0 ?
                            `<span class="station-code-badges">${codes.map(({code, color}) =>
                                `<span class="station-code-badge" style="background-color: ${color};">${code}</span>`
                            ).join('')}</span>` : '';

                        const railwayTitles = Object.keys(railwayColors[stationTitle])
                            .map(railwayTitle => `<div class="line-strip" style="background-color: ${railwayColors[stationTitle][railwayTitle]};"></div><span>${railwayTitle}</span>`)
                            .join('<br>');

                        return `${codeBadges}<strong>${stationTitle}</strong><br>${railwayTitles}`;
                    }).join('<br>'),
                    '</div>'
                ].join(''));
            }
        }
        if (addToMap) {
            popup.addTo(map);
        }
    }

    updateBusRouteVisibility() {
        const me = this,
            map = me.map;

        for (const gtfs of me.gtfs.values()) {
            const source = gtfs.id,
                shapes = new Set();

            for (const route of gtfs.routeLookup.values()) {
                if (!route.hidden) {
                    for (const shape of route.shapes) {
                        shapes.add(shape);
                    }
                }
            }
            for (const id of gtfs.featureLookup.keys()) {
                if (shapes.has(id)) {
                    map.removeFeatureState({source, id}, 'hidden');
                } else {
                    map.setFeatureState({source, id}, {hidden: true});
                }
            }
            for (const bus of gtfs.activeBusLookup.values()) {
                const route = gtfs.routeLookup.get(bus.trip.route);

                if (!route || route.hidden) {
                    me.stopBus(bus);
                }
            }
        }
        delete me.lastRefresh;
        delete me.lastRealtimeCheck;
    }

    updateBusRouteHighlight(gtfsId, routeId) {
        const me = this,
            map = me.map;

        if (gtfsId) {
            const gtfs = me.gtfs.get(gtfsId),
                route = gtfs.routeLookup.get(routeId);

            for (const id of route.shapes) {
                map.setFeatureState({source: gtfsId, id}, {'route-highlight': route.color || gtfs.color});
            }
        } else {
            for (const gtfs of me.gtfs.values()) {
                for (const id of gtfs.featureLookup.keys()) {
                    map.removeFeatureState({source: gtfs.id, id}, 'route-highlight');
                }
            }
        }
    }

    updateBusTripHighlight(object) {
        const me = this,
            {map, markedObject, trackedObject} = me,
            {gtfsId, trip} = object,
            source = gtfsId,
            id = trip.shape;

        if ((markedObject && markedObject.type === 'bus' && markedObject.gtfsId === gtfsId && markedObject.trip.shape === id) ||
            (trackedObject && trackedObject.type === 'bus' && trackedObject.gtfsId === gtfsId && trackedObject.trip.shape === id)) {
            const gtfs = me.gtfs.get(gtfsId),
                routeInfo = gtfs.routeLookup.get(trip.route),
                color = routeInfo.color,
                operator = routeInfo.operator || '',
                operatorIdx = {'SBST': 1, 'TTS': 2, 'GAS': 3, 'SMRT': 4}[operator] || 0,
                bus = markedObject && markedObject.type === 'bus' ? markedObject : trackedObject,
                nextStopIdx = bus ? bus.sectionIndex + bus.sectionLength : -1;

            map.setFeatureState({source, id}, {'trip-highlight': color || gtfs.color});

            // Highlight bus stops along this trip, mark next stop
            for (let i = 0; i < trip.stops.length; i++) {
                const stopId = trip.stops[i];
                map.setFeatureState({source, id: stopId}, {
                    'stop-highlight': true,
                    'next-stop': i === nextStopIdx,
                    'operator-idx': operatorIdx
                });
            }
        } else {
            map.removeFeatureState({source, id}, 'trip-highlight');

            // Remove stop highlights
            for (const stopId of trip.stops) {
                map.removeFeatureState({source, id: stopId}, 'stop-highlight');
                map.removeFeatureState({source, id: stopId}, 'next-stop');
            }
        }
    }

    addStationOutline(object, name) {
        const me = this,
            id = object.stations[0].id;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(me.map, `${name}-${zoom}`, {
                data: helpersGeojson.featureFilter(me.featureCollection, p => p.zoom === zoom && p.ids && p.ids[0] === id),
                opacity: 1,
                visible: true
            });
        }
    }

    removeStationOutline(name) {
        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `${name}-${zoom}`, {
                visible: false
            });
        }
    }

    refreshStationOutline() {
        const p = performance.now() % 1500 / 1500 * 2;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `stations-selected-${zoom}`, {
                opacity: p < 1 ? p : 2 - p,
                visible: true
            });
        }
    }

    setSectionData(train, index, final) {
        const stations = train.r.stations,
            {direction, timetable} = train,
            destination = (train.ds || [])[0],
            delay = train.delay || 0,
            now = this.clock.getTimeOffset();
        let arrivalTimes, departureTimes, ttIndex, departureStation, arrivalStation, currentSection, nextSection, finalSection;

        if (timetable) {
            const s = timetable.stations;

            arrivalTimes = timetable.arrivalTimes;
            departureTimes = timetable.departureTimes;
            ttIndex = helpers.valueOrDefault(index, departureTimes.reduce(
                (acc, cur, i) => cur !== undefined && cur + delay <= now ? i : acc, 0
            ));
            departureStation = s[ttIndex];
            arrivalStation = s[ttIndex + 1];
        } else {
            departureStation = train.fs || train.ts;
            arrivalStation = train.ts || train.fs;
        }

        if (direction > 0) {
            currentSection = stations.indexOf(departureStation);
            nextSection = stations.indexOf(arrivalStation, currentSection);
            finalSection = stations.indexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = stations.length - 1;
            }
        } else {
            currentSection = stations.lastIndexOf(departureStation);
            nextSection = stations.lastIndexOf(arrivalStation, currentSection);
            finalSection = stations.lastIndexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = 0;
            }
        }

        if (timetable) {
            train.timetableIndex = ttIndex;
            train.departureStation = departureStation;
            train.departureTime = helpers.valueOrDefault(departureTimes[ttIndex], arrivalTimes[ttIndex]);

            if (currentSection >= 0 && nextSection >= 0) {
                train.sectionIndex = currentSection;
                train.sectionLength = nextSection - currentSection;
                train.arrivalStation = arrivalStation;
                train.arrivalTime = arrivalTimes[ttIndex + 1];
                train.nextDepartureTime = departureTimes[ttIndex + 1];

                return true;
            }

        } else {
            let actualSection;

            if (train.sectionIndex === undefined) {
                actualSection = nextSection = currentSection;
            } else {
                actualSection = train.sectionIndex + train.sectionLength;
            }

            train.departureStation = departureStation;

            if (actualSection >= 0 && actualSection !== finalSection && ((!final && nextSection >= 0) || (final && finalSection >= 0))) {
                train.sectionIndex = actualSection;
                train.sectionLength = (final ? finalSection : nextSection) - actualSection;
                train.arrivalStation = arrivalStation === departureStation ? stations[actualSection + direction] : arrivalStation;

                return true;
            }
        }

        train.arrivalStation = train.arrivalTime = undefined;
    }

    setBusSectionData(bus, index, final) {
        const stopSequences = bus.trip.stopSequences,
            now = this.clock.getTimeOffset();
        let departureTimes, currentSection;

        if (bus.stop === undefined) {
            departureTimes = bus.trip.departureTimes;
            currentSection = helpers.valueOrDefault(index, departureTimes.reduce(
                (acc, cur, i) => cur <= now ? i : acc, 0
            ));
        } else {
            currentSection = stopSequences.indexOf(bus.stop);
        }

        // Guard for an unexpected error
        // Potential data quality issue
        if (currentSection === -1) {
            console.log('no bus stop', bus);
        }

        const finalSection = stopSequences.length - 1;

        if (bus.stop === undefined) {
            if (currentSection !== finalSection) {
                bus.sectionIndex = currentSection;
                bus.sectionLength = 1;
                bus.departureTime = departureTimes[currentSection];
                bus.nextDepartureTime = departureTimes[currentSection + 1];

                return true;
            }
        } else {
            let actualSection, nextSection;

            if (bus.sectionIndex === undefined) {
                actualSection = nextSection = currentSection;
            } else {
                actualSection = bus.sectionIndex + bus.sectionLength;
                nextSection = Math.min(currentSection + 1, finalSection);
            }

            if (!final && actualSection !== finalSection) {
                bus.sectionIndex = actualSection;
                bus.sectionLength = nextSection - actualSection;

                return true;
            }
        }
    }
}

function initContainer(container) {
    const map = helpers.createElement('div', {
        id: 'map'
    }, container);

    helpers.createElement('div', {
        id: 'loader',
        className: 'loader-inner ball-pulse',
        innerHTML: '<div></div><div></div><div></div>'
    }, container);
    helpers.createElement('div', {
        id: 'loading-error'
    }, container);
    container.classList.add('mini-singapore-3d');
    return map;
}

function showLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 1;
    element.style.display = 'block';
}

function hideLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 0;
    setTimeout(() => {
        element.style.display = 'none';
    }, 1000);
}

function showErrorMessage(container) {
    const loaderElement = container.querySelector('#loader'),
        errorElement = container.querySelector('#loading-error');

    loaderElement.style.display = 'none';
    errorElement.innerHTML = 'Loading failed. Please reload the page.';
    errorElement.style.display = 'block';
}

/**
 * Simulate bus load based on time and route.
 * In production, this would come from LTA Bus Arrival API 'Load' field.
 * @returns {string} 'SEA' (Seats Available), 'SDA' (Standing), or 'LSD' (Limited Standing)
 */
function simulateBusLoad(now, trip) {
    // Peak hours: 7-9am (25200000-32400000ms) and 5-7pm (50400000-57600000ms) from 3am base
    const isPeak = (now >= 14400000 && now <= 21600000) || (now >= 50400000 && now <= 57600000);
    const hash = (trip.id.charCodeAt(0) + trip.id.charCodeAt(trip.id.length - 1)) % 10;

    if (isPeak) {
        return hash < 3 ? 'LSD' : hash < 7 ? 'SDA' : 'SEA';
    }
    return hash < 1 ? 'LSD' : hash < 3 ? 'SDA' : 'SEA';
}

function easeInQuad(t) {
    return t * t;
}

function easeOutQuart(t) {
    return -((t -= 1) * t * t * t - 1);
}

function createInterpolant(xs, ys) {
    const length = xs.length;

    // Get consecutive differences and slopes
    const dys = [], dxs = [], ms = [];
    for (let i = 0; i < length - 1; i++) {
        const dx = xs[i + 1] - xs[i], dy = ys[i + 1] - ys[i];
        dxs.push(dx); dys.push(dy); ms.push(dy / dx);
    }

    // Get degree-1 coefficients
    const c1s = [ms[0]];
    for (let i = 0; i < dxs.length - 1; i++) {
        const m = ms[i], mNext = ms[i + 1];
        if (m * mNext <= 0) {
            c1s.push(0);
        } else {
            const dx_ = dxs[i], dxNext = dxs[i + 1], common = dx_ + dxNext;
            c1s.push(3 * common / ((common + dxNext) / m + (common + dx_) / mNext));
        }
    }
    c1s.push(ms[ms.length - 1]);

    // Get degree-2 and degree-3 coefficients
    const c2s = [], c3s = [];
    for (let i = 0; i < c1s.length - 1; i++) {
        const c1 = c1s[i], m_ = ms[i], invDx = 1 / dxs[i], common_ = c1 + c1s[i + 1] - m_ - m_;
        c2s.push((m_ - c1 - common_) * invDx); c3s.push(common_ * invDx * invDx);
    }

    // Return interpolant function
    return x => {
        // The rightmost point in the dataset should give an exact result
        let i = xs.length - 1;
        if (x === xs[i]) {
            return ys[i];
        }

        // Search for the interval x is in, returning the corresponding y if x is one of the original xs
        let low = 0, mid, high = c3s.length - 1;
        while (low <= high) {
            mid = Math.floor(0.5 * (low + high));
            const xHere = xs[mid];
            if (xHere < x) {
                low = mid + 1;
            } else if (xHere > x) {
                high = mid - 1;
            } else {
                return ys[mid];
            }
        }
        i = Math.max(0, high);

        // Interpolate
        const diff = x - xs[i], diffSq = diff * diff;
        return ys[i] + c1s[i] * diff + c2s[i] * diffSq + c3s[i] * diff * diffSq;
    };
}

function getLayerZoom(zoom) {
    return helpers.clamp(Math.floor(zoom), 13, 18);
}

// Deprecated
// function getObjectScale(zoom) {
//     return Math.pow(2, 14 - helpers.clamp(zoom, 13, 19));
// }

function isVehicle(object) {
    return object && helpers.includes(['train', 'flight', 'bus'], object.type);
}

function isStation(object) {
    return object && object.type === 'station';
}

function isEqualObject(a, b) {
    if (a === b) {
        return true;
    }
    if (a && a.properties && b && b.properties && a.properties.ids === b.properties.ids) {
        return true;
    }
    return false;
}
